// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Five routes for two subjects. Both detail views are pages and not modals,
// because both are places people link each other to, come back to, and keep
// open in a tab while they are on the phone to the supplier.

export const routes = [
  // ── Purchases ──────────────────────────────────────────────────────────────
  {
    path: '/purchases',
    name: 'purchases',
    component: () => import('./pages/PurchasesPage.vue'),
    meta: { permission: 'purchasing:read', title: 'purchasing.purchases.title' },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  {
    path: '/purchases/new',
    name: 'purchase-new',
    component: () => import('./pages/PurchaseEditorPage.vue'),
    meta: { permission: 'purchasing:write', title: 'purchasing.purchases.newTitle' },
  },
  {
    path: '/purchases/:id',
    name: 'purchase',
    component: () => import('./pages/PurchaseEditorPage.vue'),
    meta: { permission: 'purchasing:read', title: 'purchasing.purchases.detailTitle' },
  },

  // ── Suppliers ──────────────────────────────────────────────────────────────
  {
    path: '/suppliers',
    name: 'suppliers',
    component: () => import('./pages/SuppliersPage.vue'),
    meta: { permission: 'purchasing:read', title: 'purchasing.suppliers.title' },
  },
  // A page and not a modal, on purpose: twenty fields scrolling inside a dialog
  // is a form fighting its frame.
  {
    path: '/suppliers/new',
    name: 'supplier-new',
    component: () => import('./pages/SupplierFormPage.vue'),
    meta: { permission: 'purchasing:suppliers', title: 'purchasing.suppliers.newTitle' },
  },
  {
    path: '/suppliers/:id',
    name: 'supplier',
    component: () => import('./pages/SupplierPage.vue'),
    meta: { permission: 'purchasing:read', title: 'purchasing.suppliers.detailTitle' },
  },
  {
    path: '/suppliers/:id/edit',
    name: 'supplier-edit',
    component: () => import('./pages/SupplierFormPage.vue'),
    meta: { permission: 'purchasing:suppliers', title: 'purchasing.suppliers.editTitle' },
  },

  // ── Requisitions ───────────────────────────────────────────────────────────
  // Anybody who takes part: whoever asks (purchasing:request) sees theirs,
  // purchasing sees and decides all of them. A list means ANY of them.
  {
    path: '/purchase-requisitions',
    name: 'purchase-requisitions',
    component: () => import('./pages/RequisitionsPage.vue'),
    meta: {
      permission: ['purchasing:request', 'purchasing:read', 'purchasing:approve', 'purchasing:approve_high'],
      title: 'purchasing.requisitions.title',
    },
  },
  {
    path: '/purchase-requisitions/new',
    name: 'purchase-requisition-new',
    component: () => import('./pages/RequisitionPage.vue'),
    meta: { permission: ['purchasing:request', 'purchasing:write', 'purchasing:approve'], title: 'purchasing.requisitions.newTitle' },
  },
  {
    path: '/purchase-requisitions/:id',
    name: 'purchase-requisition',
    component: () => import('./pages/RequisitionPage.vue'),
    meta: {
      permission: ['purchasing:request', 'purchasing:read', 'purchasing:approve', 'purchasing:approve_high'],
      title: 'purchasing.requisitions.detailTitle',
    },
  },

  // ── Contracts ──────────────────────────────────────────────────────────────
  // One page for a contract, read and edited in place: it is a short record
  // with one table, and it is opened to change a price far more often than
  // to read it.
  {
    path: '/supplier-contracts',
    name: 'supplier-contracts',
    component: () => import('./pages/ContractsPage.vue'),
    meta: { permission: 'purchasing:read', title: 'purchasing.contracts.title' },
  },
  {
    path: '/supplier-contracts/new',
    name: 'supplier-contract-new',
    component: () => import('./pages/ContractPage.vue'),
    meta: { permission: 'purchasing:suppliers', title: 'purchasing.contracts.newTitle' },
  },
  {
    path: '/supplier-contracts/:id',
    name: 'supplier-contract',
    component: () => import('./pages/ContractPage.vue'),
    meta: { permission: 'purchasing:read', title: 'purchasing.contracts.detailTitle' },
  },

  // ── Comparativos de ofertas y precios ofertados (0.12.0) ───────────────────
  // Vinieron de Obras: lo que se hace aquí es comprar, aunque solo tenga sentido
  // con paquetes de obra. Por eso las cuatro llevan `requires: ['construction']`
  // —sin Obras la pantalla no existe en la instancia y la ruta acaba en «no
  // encontrado»— y el backend contesta 404 a lo mismo.
  {
    path: '/tenders',
    name: 'tenders',
    component: () => import('./pages/TendersPage.vue'),
    meta: { permission: 'purchasing:read', requires: ['construction'], title: 'purchasing.tenders.list.title' },
  },
  {
    // La puerta desde el paquete de una obra: abre el suyo o lleva al que hay.
    path: '/tenders/new',
    name: 'tender-open',
    component: () => import('./pages/TenderOpenPage.vue'),
    meta: { permission: 'purchasing:read', requires: ['construction'], title: 'purchasing.tenders.opening.title' },
  },
  {
    path: '/tenders/:id',
    name: 'tender',
    component: () => import('./pages/TenderPage.vue'),
    meta: { permission: 'purchasing:read', requires: ['construction'], title: 'purchasing.tenders.title' },
  },
  {
    path: '/tender-prices',
    name: 'tender-prices',
    component: () => import('./pages/TenderPricesPage.vue'),
    meta: { permission: 'purchasing:read', requires: ['construction'], title: 'purchasing.tenderPrices.title' },
  },
]

/**
 * One row that opens, and not two.
 *
 * The work of this module happens on the purchases screen; the supplier file is
 * where you go once, when somebody new turns up. Giving them a sidebar entry
 * each would spend two of the few slots the next module installed has the same
 * claim to. The child is an ordinary entry and the shell filters it by
 * permission before drawing it.
 */
export const navigation = [
  {
    to: '/purchases',
    labelKey: 'purchasing.title',
    // Also for whoever only ASKS for purchases: the maintenance lead has to
    // find «Solicitudes» without being able to see a single order.
    permission: ['purchasing:read', 'purchasing:request', 'purchasing:approve', 'purchasing:approve_high'],
    icon: 'cart',
    group: 'business',
    children: [
      { to: '/purchase-requisitions', labelKey: 'purchasing.requisitions.title', permission: ['purchasing:request', 'purchasing:read', 'purchasing:approve', 'purchasing:approve_high'] },
      { to: '/suppliers', labelKey: 'purchasing.suppliers.title', permission: 'purchasing:read' },
      { to: '/supplier-contracts', labelKey: 'purchasing.contracts.title', permission: 'purchasing:read' },
      // Solo con Obras instalado: sin paquetes de obra no hay nada que licitar.
      { to: '/tenders', labelKey: 'purchasing.tenders.menu', permission: 'purchasing:read', requires: ['construction'] },
      { to: '/tender-prices', labelKey: 'purchasing.tenderPrices.menu', permission: 'purchasing:read', requires: ['construction'] },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client. That includes what this module is called on the
// PLATFORM's own screens — Roles, Settings, Audit and the assistant panel —
// because a module that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    purchasing: {
      title: 'Compras',
      // La pestaña propia de una ficha cuando otros módulos cuelgan las suyas.
      tabs: { overview: 'Ficha' },
      // Las tarjetas del asistente (backend/assistant.js): el borrador, la
      // solicitud de compra y la decisión sobre ella.
      assistant: {
        draft: {
          summary: {
            order: 'Abrir un borrador de pedido con {count} líneas para {supplier}',
            price_request: 'Abrir un borrador de petición de precio con {count} líneas para {supplier}',
          },
          action: {
            order: 'Abrir borrador de pedido',
            price_request: 'Abrir borrador de petición de precio',
          },
          where: 'Proveedor',
          after: 'Queda en borrador: no sale nada hasta que alguien lo envíe',
          moreLines: 'Y {count} líneas más',
          outcome: 'Borrador {code} abierto',
        },
        request: {
          summary: 'Pedir a compras: {title} ({count} líneas)',
          action: 'Pedir a compras',
          noLines: 'Aún no hay nada que comprar: di qué y cuánto',
          noQuantity: 'Cada línea necesita una cantidad',
          supplierUnlinked: '«{supplier}» no está en la ficha de proveedores: va como nota',
          after: 'Compras la aprobará o dirá por qué no',
          afterHigh: 'Pasa del tope: después de compras la aprueba también dirección',
          outcome: 'Solicitud {code} enviada a compras',
        },
        approveRequest: {
          summary: 'Aprobar la solicitud {code} · {title}',
          action: 'Aprobar solicitud de compra',
          outcome: 'Solicitud {code} aprobada',
          outcomeHigh: '{code} aprobada por compras: ahora decide dirección',
        },
        rejectRequest: {
          summary: 'Rechazar la solicitud {code} · {title}',
          action: 'Rechazar solicitud de compra',
          outcome: 'Solicitud {code} rechazada',
        },
        decide: {
          where: 'Solicitud de compra',
          notFound: 'No está claro qué solicitud es: elígela en Solicitudes de compra',
        },
        field: {
          supplier: 'Proveedor',
          title: 'Qué es',
          neededBy: 'Para cuándo',
          total: 'Base imponible',
          destination: 'Adónde',
          reason: 'Para qué',
          estimate: 'Estimación',
          request: 'Solicitud',
          requestedBy: 'Pedida por',
          note: 'Nota',
        },
      },

      dashboard: {
        open: 'Pedidos en curso',
        openHelp: 'Cuántos pedidos a proveedor están comprometidos y no han llegado del todo.',
        committed: 'Comprometido',
        committedHelp: 'Lo que suman los pedidos en curso, en euros: gasto ya comprometido que aún no ha entrado.',
        suppliers: 'Proveedores activos',
        suppliersHelp: 'Cuántos proveedores están activos o marcados como preferentes.',
        pendingAmount: 'Pendiente con proveedores',
        pendingAmountHelp: 'Lo recibido de proveedores que todavía no os han facturado, en euros.',
        contractsEnding: 'Contratos que caducan',
        contractsEndingHelp: 'Los contratos con proveedores dentro de su plazo de aviso o recién caducados: los que hay que renegociar.',
        requisitions: 'Solicitudes por aprobar',
        requisitionsHelp: 'Las solicitudes de compra que esperan tu aprobación.',
        awaiting: 'Esperando mercancía',
        awaitingHelp: 'Los pedidos en curso más recientes, con su proveedor. Estira la tarjeta y caben más.',
      },
      actions: {
        newOrder: 'Nuevo pedido', newRequest: 'Pedir precio', newSupplier: 'Nuevo proveedor',
        newDeliveryNote: 'Albarán sin pedido',
        edit: 'Editar', save: 'Guardar', cancel: 'Cancelar', close: 'Cerrar', remove: 'Eliminar',
        discard: 'Descartar',
        archive: 'Archivar', restore: 'Devolver al fichero', addLine: 'Añadir línea', pdf: 'Descargar PDF',
        send: 'Marcar como enviado', confirm: 'Confirmar', receive: 'Recibir mercancía',
        convert: 'Convertir en pedido', cancelDoc: 'Anular', reopen: 'Reabrir',
        markAnswered: 'Marcar como respondida', markClosed: 'Cerrar solicitud',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      purchases: {
        title: 'Compras',
        subtitle: 'Lo que habéis pedido y lo que estáis esperando',
        newTitle: 'Nueva compra', detailTitle: 'Compra',
        deliveryNoteTitle: 'Albarán sin pedido',
        kind: { order: 'Pedido', price_request: 'Solicitud de precio' },
        status: {
          draft: 'Borrador', sent: 'Enviado', answered: 'Respondida', confirmed: 'Confirmado',
          partial: 'Parcial', received: 'Recibido', closed: 'Cerrada', cancelled: 'Anulado',
        },
        filters: {
          search: 'Buscar', searchPlaceholder: 'Número, proveedor, referencia…',
          kind: 'Tipo', allKinds: 'Todos', status: 'Estado', allStatuses: 'Todos',
          onlyOpen: 'Solo abiertos', onlyOverdue: 'Solo con retraso',
          only: 'Solo las compras de {name}', showAll: 'Ver todas',
          oneCustomer: 'este cliente', oneWork: 'esta obra',
        },
        table: {
          code: 'Número', supplier: 'Proveedor', title: 'Concepto', expected: 'Prevista',
          lines: 'Líneas', total: 'Total', received: 'Recibido', status: 'Estado',
        },
        kpi: {
          open: 'Pedidos abiertos', committed: 'Comprometido',
          overdue: 'Con retraso', received: 'Recibidos',
        },
        empty: {
          none: 'Todavía no hay compras',
          noneHint: 'Empieza pidiendo precio a un proveedor, o abre un pedido directamente.',
          filtered: 'Sin resultados',
          filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
          noLines: 'Este documento no tiene líneas',
          noLinesHint: 'Añade lo que hay que comprar antes de mandarlo.',
        },
        overdueBy: 'Con {days} días de retraso',
        lineOf: '{received} de {ordered}',
        receivedHint: 'Líneas que han llegado enteras. El importe recibido lo lleva la obra.',
        created: 'Compra creada', saved: 'Compra guardada',
        statusChanged: 'Estado actualizado', archived: 'Compra archivada',
        restored: 'Compra devuelta a las listas',
        converted: 'Pedido {code} creado a partir de la solicitud',
        blockedSupplier: 'Este proveedor está bloqueado',
        search: { subtitle: 'Ver el documento' },
      },
      suppliers: {
        title: 'Proveedores',
        subtitle: 'La ficha de cada empresa a la que compráis',
        newTitle: 'Nuevo proveedor', editTitle: 'Editar proveedor', detailTitle: 'Ficha de proveedor',
        loading: 'Cargando…',
        status: {
          active: 'Activo', preferred: 'Preferente', inactive: 'Inactivo', blocked: 'Bloqueado',
        },
        filters: {
          search: 'Buscar', searchPlaceholder: 'Nombre, CIF, población…',
          status: 'Estado', allStatuses: 'Todos', category: 'Categoría', allCategories: 'Todas',
        },
        table: {
          name: 'Empresa', taxId: 'CIF / NIF', category: 'Categoría', city: 'Población',
          contact: 'Contacto', leadTime: 'Plazo', ordered: 'Comprado', status: 'Estado',
        },
        kpi: {
          active: 'Activos', preferred: 'Preferentes', total: 'Total',
          ordered: 'Comprado', invoiced: 'Facturado',
        },
        empty: {
          none: 'Todavía no hay proveedores',
          noneHint: 'Da de alta el primero: sin proveedor no se puede pedir nada.',
          filtered: 'Sin resultados',
          filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
          noPurchases: 'Todavía no le habéis comprado nada',
        },
        details: 'Datos de contacto',
        terms: 'Condiciones de compra',
        address: 'Dirección',
        activity: 'Actividad',
        lastPurchases: 'Últimas compras',
        created: 'Proveedor dado de alta', saved: 'Proveedor guardado',
        archive: {
          title: 'Archivar proveedor',
          body: 'Vas a archivar a {name}.',
          keeps: 'No se borra nada: deja de aparecer en las listas y su historial sigue intacto.',
          done: 'Proveedor archivado',
          restored: 'Proveedor devuelto al fichero',
          isArchived: 'Este proveedor está archivado: no sale en ninguna lista y no se le puede comprar. Devolverlo lo pone otra vez en el fichero, pero INACTIVO — volver a comprarle es la segunda decisión.',
          blocked: 'Tiene compras abiertas: ciérralas o anúlalas antes de archivarlo.',
        },
        search: { subtitle: 'Ver su ficha' },
      },
      related: {
        purchases: 'Compras y recepciones',
        subcontracts: 'Subcontratas',
        forCustomer: 'Compras',
      },
      spendType: {
        material: 'Material', plant: 'Alquiler de maquinaria',
        subcontract: 'Subcontrata', service: 'Servicio', other: 'Otros',
      },
      fields: {
        name: 'Razón social', namePlaceholder: 'Nombre completo de la empresa',
        shortName: 'Nombre corto', shortNameHint: 'El que usáis en las listas',
        taxId: 'CIF / NIF', status: 'Estado', category: 'Categoría',
        categoryPlaceholder: 'Ej.: Ferretería, transporte, acero',
        tags: 'Etiquetas', rating: 'Valoración', noRating: 'Sin valorar',
        email: 'Correo', phone: 'Teléfono', website: 'Web',
        address: 'Calle', postalCode: 'CP', city: 'Población', country: 'País',
        paymentTerms: 'Forma de pago', paymentMethod: 'Medio de pago', currency: 'Moneda',
        discount: 'Descuento', discountHint: 'El que os hacen sobre tarifa, en %',
        leadTime: 'Plazo de entrega', leadTimeHint: 'Días desde el pedido. 0 si no lo sabéis',
        minOrder: 'Pedido mínimo', iban: 'IBAN',
        supplierRef: 'Nuestro código con ellos',
        supplierRefHint: 'El número de cuenta que usan ellos para nosotros; sale en sus facturas',
        notes: 'Notas', createdAt: 'Alta',
        contactName: 'Nombre', contactPosition: 'Cargo',
        contactPositionPlaceholder: 'Ej.: Comercial de zona',
        supplier: 'Proveedor', customer: 'Cliente',
        customerHint: 'Si esto se compra para un cliente concreto',
        customerPlaceholder: 'Nombre del cliente',
        work: 'Obra', workPlaceholder: 'Buscar una obra',
        workHint: 'Para qué obra se compra. Con ella puesta, el albarán entra solo en el coste de la obra en vez de teclearse.',
        package: 'Paquete', packagePlaceholder: 'Buscar un paquete',
        packageHint: 'Contra qué paquete de contratación. Es lo que hace que este pedido descuente del disponible correcto: al elegirlo se ve cuánto queda antes de firmar.',
        spendType: 'Clase de gasto',
        spendTypeHint: 'Separa material de alquiler y de subcontrata, que es lo que se mira cuando una partida se desvía.',
        kind: 'Tipo de documento', concept: 'Concepto',
        conceptPlaceholder: 'Para qué es esta compra, en pocas palabras',
        reference: 'Referencia del proveedor',
        referenceHint: 'Su presupuesto, su proforma: lo que os han pasado ellos',
        issueDate: 'Fecha', expectedDate: 'Fecha prevista',
        receivedAt: 'Fecha de entrega',
        receivedAtHint: 'El día que llegó el material. De ese día es el coste, no de hoy.',
        deliveryAddress: 'Dirección de entrega',
        deliveryHint: 'Déjala vacía si va a vuestra dirección de siempre',
        invoiceRef: 'Factura del proveedor',
        notesForSupplier: 'Notas para el proveedor',
        notesForSupplierHint: 'Esto se imprime en el documento que reciben',
        internalNotes: 'Notas internas', internalNotesHint: 'Esto no sale en el documento',
      },
      lines: {
        title: 'Líneas', ref: 'Ref.', description: 'Descripción',
        quantity: 'Cant.', unit: 'Unidad', unitPrice: 'Precio', discount: 'Dto.',
        tax: 'IVA', total: 'Total', received: 'Recibido',
        descriptionPlaceholder: 'Qué se pide',
        subtotal: 'Base imponible', discountTotal: 'Descuentos', taxTotal: 'IVA', grandTotal: 'Total',
      },
      contacts: {
        title: 'Personas de contacto',
        add: 'Añadir contacto', addTitle: 'Añadir contacto', editTitle: 'Editar contacto',
        none: 'Todavía no hay contactos',
        noneHint: 'La persona a quien llamáis cuando pasa algo con un pedido.',
        primary: 'Principal', makePrimary: 'Hacer principal', markPrimary: 'Marcar como contacto principal',
        primaryHint: 'Es el nombre que sale en los pedidos. Solo puede haber uno.',
        removeTitle: 'Eliminar contacto', removeBody: '¿Eliminar a {name} de este proveedor?',
        removed: 'Contacto eliminado', saved: 'Contacto guardado',
      },
      files: {
        title: 'Papeles del pedido',
        add: 'Subir un documento', uploading: 'Subiendo…', uploaded: 'Documento subido',
        whatIsIt: '¿Qué es?',
        kind: { delivery_note: 'Albarán', invoice: 'Factura', other: 'Otro' },
        dropTitle: 'Quitar el documento',
        dropConfirm: '«{name}» deja de estar en este pedido.',
        none: {
          title: 'Sin papeles',
          subtitle: 'Sube aquí el albarán de cada entrega y la factura cuando llegue.',
        },
      },
      receive: {
        title: 'Recibir mercancía',
        hint: 'Apunta lo que ha llegado en ESTE albarán, no el total acumulado.',
        pending: 'Pendiente', arrived: 'Llega ahora', all: 'Todo lo que falta',
        deliveryNote: 'El albarán',
        deliveryNoteHint: 'Súbelo ahora, que es cuando lo tienes en la mano.',
        pickFile: 'Elegir el fichero…',
        date: 'Fecha del albarán', note: 'Nota del albarán',
        notePlaceholder: 'Ej.: dos cajas golpeadas, aceptadas',
        done: 'Recepción registrada',
        complete: 'Pedido completo', partial: 'Recepción parcial',
        nothing: 'No has marcado nada como recibido',
      },
      requisitions: {
        title: 'Solicitudes',
        subtitle: 'Lo que alguien necesita que se compre, hasta que compras lo aprueba y lo pide',
        new: 'Nueva solicitud',
        newTitle: 'Nueva solicitud de compra',
        detailTitle: 'Solicitud de compra',
        newHint: 'Cuenta qué necesitas: compras lo revisa y te avisa.',
        search: 'Buscar por número, qué se pide o quién lo pide…',
        send: 'Enviar solicitud',
        sent: 'Solicitud {code} enviada a compras',
        saved: 'Solicitud guardada',
        approved: 'Solicitud aprobada',
        rejected: 'Solicitud rechazada',
        cancelled: 'Solicitud retirada',
        converted: 'Creado {code}: revisa precios y proveedor antes de enviarlo',
        cancelTitle: '¿Retirar la solicitud?',
        cancelBody: 'La solicitud {code} deja de estar pendiente. Queda en el historial.',
        scope: {
          toApprove: 'Por aprobar',
          mine: 'Mis solicitudes',
          all: 'Todas',
        },
        empty: {
          toApprove: 'No hay nada esperando tu aprobación.',
          mine: 'Todavía no has pedido nada.',
          mineHint: 'Cuando necesites que se compre algo, pídelo aquí: compras lo verá al momento.',
          all: 'No hay solicitudes.',
        },
        table: {
          what: 'Qué se pide',
          neededBy: 'Para cuándo',
          estimate: 'Estimado',
          status: 'Estado',
        },
        status: {
          pending: 'Pendiente',
          awaiting_direction: 'Falta dirección',
          approved: 'Aprobada',
          rejected: 'Rechazada',
          converted: 'Pedida',
          cancelled: 'Retirada',
        },
        statusHint: {
          pending: 'Esperando a que compras la revise.',
          awaiting_direction: 'Compras la ha aprobado; por su importe falta el visto bueno de dirección.',
          approved: 'Aprobada: compras la convertirá en petición de precio o en pedido.',
          rejected: 'No se va a comprar. El motivo está en el historial.',
          converted: 'Ya está en marcha en el documento',
          cancelled: 'Se retiró antes de comprarse.',
        },
        fields: {
          title: '¿Qué necesitas?',
          titlePlaceholder: 'Radial de 230 mm para el taller',
          reason: 'Para qué',
          reasonHint: 'Lo primero que preguntará compras: cuéntalo aquí y te ahorras la llamada.',
          neededBy: 'Para cuándo',
          destination: 'Dónde va',
          destinationHint: 'Una máquina, una furgoneta, un taller…',
          destinationPlaceholder: 'Taller 2',
          supplier: 'Proveedor sugerido',
          supplierHint: 'Si sabes dónde lo tienen. Compras decide.',
        },
        lines: {
          title: 'Lo que se pide',
          description: 'Descripción',
          descriptionPlaceholder: 'Radial Makita 230 mm',
          materialPlaceholder: 'Busca en el almacén o escribe qué hace falta',
          locationPlaceholder: 'Ubicación de destino (opcional)',
          inStock: 'En almacén: {n} {unit}',
          quantity: 'Cantidad',
          unit: 'Unidad',
          estimate: 'Precio aprox.',
          total: 'Importe estimado',
          totalHint: 'Orientativo: el precio real lo pone compras.',
        },
        actions: {
          approve: 'Aprobar',
          approveHigh: 'Aprobar (dirección)',
          reject: 'Rechazar',
          convert: 'Convertir en pedido…',
          cancel: 'Retirar',
        },
        reject: {
          title: 'Rechazar la solicitud',
          reason: 'Motivo',
          placeholder: 'Ya hay una en el almacén de la nave 1…',
        },
        convert: {
          title: 'Comprar lo solicitado',
          kind: 'Qué se crea',
          order: 'Pedido',
          priceRequest: 'Petición de precio',
          hint: 'Se crea en borrador con estas líneas y los precios estimados: los revisas antes de enviarlo.',
          go: 'Crear',
        },
        history: {
          title: 'Historial',
          requested: 'la solicitó',
          edited: 'la modificó',
          approved_purchasing: 'la aprobó (compras)',
          approved: 'la aprobó',
          rejected: 'la rechazó',
          cancelled: 'la retiró',
          converted: 'la convirtió en',
        },
      },
      contracts: {
        title: 'Contratos',
        subtitle: 'Lo pactado con cada proveedor: precios, importe comprometido y vigencia',
        new: 'Nuevo contrato',
        newTitle: 'Nuevo contrato',
        detailTitle: 'Contrato',
        loading: 'Cargando…',
        created: 'Contrato creado',
        saved: 'Contrato guardado',
        fields: {
          supplier: 'Proveedor',
          title: 'Nombre del contrato',
          titlePlaceholder: 'Acuerdo marco 2026, tarifa de tubería…',
          reference: 'Su número de contrato',
          referenceHint: 'El que figura en su papel. Sale impreso en cada pedido hecho con este contrato.',
          startsOn: 'Vigente desde',
          endsOn: 'Vigente hasta',
          noticeDays: 'Avisar con (días)',
          noticeHint: 'Cuántos días antes del final avisa el ERP, para renegociar a tiempo.',
          committedAmount: 'Importe comprometido',
          committedHint: 'Lo que os habéis comprometido a comprar, sin IVA. Déjalo en 0 si solo se pactaron precios.',
          notes: 'Notas internas',
        },
        state: {
          upcoming: 'Aún no vigente',
          active: 'Vigente',
          expiring: 'Caduca pronto',
          expired: 'Caducado',
        },
        table: {
          contract: 'Contrato',
          supplier: 'Proveedor',
          validity: 'Vigencia',
          consumed: 'Consumido',
          state: 'Estado',
        },
        kpi: {
          inForce: 'Vigentes',
          expiring: 'Caducan pronto',
          expired: 'Caducados',
          committed: 'Comprometido vigente',
        },
        filters: {
          search: 'Buscar por nombre, número o proveedor…',
          show: 'Mostrar',
          all: 'Todos',
          inForce: 'Solo vigentes',
          archived: 'Archivados',
        },
        empty: {
          none: 'Todavía no hay contratos',
          noneHint: 'Da de alta lo pactado con un proveedor: sus precios se aplicarán solos al hacerle un pedido.',
          filtered: 'Ningún contrato coincide',
          filteredHint: 'Prueba a quitar el filtro o a buscar otra cosa.',
        },
        usage: {
          title: 'Consumo',
          of: '{consumed} de {committed}',
          remaining: 'Quedan {amount}',
          over: 'Superado en {amount}',
          noCommitment: 'Sin importe comprometido: el contrato solo fija precios.',
          orders: '{n} pedido(s) con este contrato',
          consumedHint: 'Pedidos enviados o posteriores, sin IVA. Los borradores y los anulados no cuentan.',
        },
        days: {
          left: 'Quedan {n} días',
          today: 'Termina hoy',
          ago: 'Terminó hace {n} días',
          startsIn: 'Empieza dentro de {n} días',
          until: 'Hasta el {date}',
          from: 'Desde el {date}',
        },
        prices: {
          title: 'Precios pactados',
          hint: 'Una línea de pedido con la misma referencia —o, sin referencia, la misma descripción— lleva este precio mientras el contrato esté vigente.',
          add: 'Añadir precio',
          fromHistory: 'Desde compras anteriores',
          none: 'Sin precios pactados',
          noneHint: 'Añade las referencias con su precio. Un contrato sin precios solo lleva la cuenta del importe.',
          ref: 'Ref.',
          description: 'Descripción',
          unit: 'Unidad',
          unitPrice: 'Precio',
          discount: 'Dto. %',
          count: '{n} precio(s) pactado(s)',
        },
        files: {
          title: 'Contrato firmado',
          add: 'Subir documento',
          uploading: 'Subiendo…',
          none: 'Todavía no se ha subido el contrato firmado',
          noneHint: 'Súbelo en PDF: es lo que se busca cuando un precio no cuadra con la factura.',
          kind: {
            signed: 'Firmado',
            other: 'Otro',
          },
          whatIsIt: 'Qué es',
          uploaded: 'Documento subido',
          dropTitle: 'Quitar documento',
          dropConfirm: '¿Quitar «{name}» del contrato?',
          saveFirst: 'Guarda el contrato para poder subir el documento firmado.',
        },
        purchases: {
          title: 'Pedidos hechos con este contrato',
          none: 'Todavía no se ha pedido nada con este contrato.',
        },
        archive: {
          title: 'Archivar contrato',
          body: 'El contrato {code} deja de ofrecerse al hacer pedidos. Los pedidos hechos con él lo siguen mostrando.',
          done: 'Contrato archivado',
          restored: 'Contrato devuelto',
          isArchived: 'Este contrato está archivado: no se ofrece al hacer pedidos.',
        },
        inPurchase: {
          field: 'Contrato',
          none: 'Sin contrato',
          hint: 'Sus precios pactados se aplican solos a las líneas que coinciden.',
          agreed: 'Pactado',
          agreedTitle: 'Precio del contrato {code}',
          remaining: 'Quedan {amount} de {committed} comprometidos.',
          exceeds: 'Este pedido supera en {amount} lo que queda del contrato.',
          endsOn: 'Vigente hasta el {date}.',
          pickerHeading: 'Precios pactados · {code}',
          open: 'Ver contrato',
        },
        supplier: {
          title: 'Contratos',
          none: 'Sin contratos con este proveedor.',
          add: 'Nuevo contrato',
        },
      },
      history: {
        open: 'Desde compras anteriores',
        title: 'Lo que ya hemos comprado',
        search: 'Buscar por descripción o referencia…',
        loading: 'Buscando…',
        fromSupplier: 'Comprado a {name}',
        fromOthers: 'Comprado a otros proveedores',
        all: 'Comprado antes',
        noneFromSupplier: 'A este proveedor todavía no le hemos comprado nada parecido.',
        none: 'Nada que coincida.',
        times: '{n} veces',
        last: 'último {code}, {date}',
        added: '{n} añadida(s) · última: {name}',
        done: 'Hecho',
        repeat: 'Repetir pedido',
        repeated: 'Borrador {code} creado con las mismas líneas',
      },
      send: {
        action: 'Enviar',
        toSupplier: 'Enviar al proveedor',
        again: 'Volver a enviar',
      },
      // Lo que «Enviar por email» pone de asunto en el redactor de la plataforma
      // (use-cases/PurchaseMail.js → subjectKey).
      mail: {
        subject: {
          order: 'Pedido {code}',
          request: 'Petición de precio {code}',
        },
      },
      cancel: {
        title: 'Anular documento',
        body: 'Vas a anular {code}.',
        reason: 'Motivo', reasonHint: 'Queda escrito: es lo que evita que alguien lo vuelva a pedir dentro de un mes.',
        reasonPlaceholder: 'Ej.: el proveedor no puede servir en fecha',
        done: 'Documento anulado',
      },
      document: {
        order: 'Pedido de compra', priceRequest: 'Solicitud de precio',
        date: 'Fecha', expected: 'Necesario para', supplier: 'Proveedor',
        ourRef: 'Nuestra referencia', yourRef: 'Vuestra referencia', deliverTo: 'Entregar en',
        contract: 'Contrato',
        taxId: 'CIF/NIF', ref: 'Ref.', description: 'Descripción', quantity: 'Cant.',
        unitPrice: 'Precio', discount: 'Dto.', total: 'Total', subtotal: 'Base imponible',
        tax: 'IVA', grandTotal: 'Total', paymentTerms: 'Forma de pago', notes: 'Notas',
        priceRequestNote: 'Esto es una solicitud de precio. No es un pedido.',
      },
      notifications: {
        stockAlert: 'Material por debajo del mínimo',
        lateOrder: 'Un pedido que no ha llegado',
        contractExpiry: 'Un contrato con un proveedor que se acaba',
        contract: {
          notice: {
            title: 'El contrato {code} caduca pronto',
            body: '«{title}» con {supplier} termina el {endsOn}: quedan {days} días para renegociarlo.',
          },
          expired: {
            title: 'El contrato {code} ha caducado',
            body: '«{title}» con {supplier} terminó el {endsOn}. Los pedidos nuevos ya no llevan sus precios.',
          },
        },
        requisitionCategory: 'Solicitudes de compra',
        requisition: {
          toApprove: {
            title: 'Solicitud {code} por aprobar',
            body: '{who} pide: {title}.',
          },
          toDirection: {
            title: 'Solicitud {code}: falta dirección',
            body: '{title} ({who}) supera el tope y necesita tu aprobación.',
          },
          approved: {
            title: 'Tu solicitud {code} está aprobada',
            body: '{title}: compras la tramitará.',
          },
          rejected: {
            title: 'Tu solicitud {code} no se compra',
            body: '{title}. Motivo: {extra}',
          },
          converted: {
            title: 'Tu solicitud {code} está en marcha',
            body: '{title}: ya está en el documento {extra}.',
          },
        },
        lateOrders: {
          title: 'Hay pedidos que no han llegado',
          body: '{count} pedido(s) han pasado su fecha. El más viejo, {code} de {supplier}, lleva {days} día(s).',
        },
        lowStock: {
          title: 'Material por debajo del mínimo',
          body: '{material}: quedan {stock} y el mínimo son {minimum}.',
        },
        planAlert: 'El plan avisa de que faltará algo',
        planRisk: {
          material: 'Faltará material',
          lead_time: 'El plazo del proveedor ya no llega',
          supplier: 'Riesgo con un proveedor',
          body: '{subject} — {period}, según el plan {plan}.',
        },
      },
    },

    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan de compras son de este módulo. Van aquí para que instalarlo deje
    // esas tres pantallas completas sin tocar la plataforma.
    modules: { purchasing: 'Compras' },
    assistant: {
      openers: {
        purchasing: {
          'whats-late': '¿Qué estamos esperando que no ha llegado?',
          'spend-this-quarter': '¿Cuánto hemos comprado este trimestre, y a quién?',
          'draft-an-order': 'Prepárame un borrador de pedido',
          'ask-to-buy': 'Pídele a compras algo que necesito',
          'requests-waiting': '¿Qué solicitudes de compra me esperan?',
        },
      },
    },
    permissions: {
      purchasing: {
        read: 'Ver proveedores y compras',
        write: 'Crear y editar compras',
        send: 'Mandar un pedido a un proveedor',
        receive: 'Dar mercancía por recibida',
        suppliers: 'Mantener la ficha de proveedores',
        request: 'Pedir que se compre algo (sin poder comprar)',
        approve: 'Aprobar solicitudes de compra',
        approve_high: 'Aprobar solicitudes por encima del tope',
        award: 'Adjudicar comparativos',
      },
    },
    settings: {
      mail: {
      },
      ns: {
        purchasing: {
          title: 'Compras',
          subtitle: 'Con qué datos nacen un proveedor y una compra nuevos',
          defaultCountry: { label: 'País con el que nace un proveedor nuevo' },
          defaultCurrency: {
            label: 'Moneda en la que os factura un proveedor nuevo',
            help: 'ISO 4217, tres letras. Después se puede cambiar proveedor a proveedor.',
          },
          defaultPaymentTerms: { label: 'Forma de pago con la que nace un proveedor nuevo' },
          defaultTaxRate: {
            label: 'IVA con el que nace una compra nueva',
            help: 'El que hereda cada línea si no dice otra cosa. 0, 4, 10 o 21.',
          },
          defaultLeadTimeDays: {
            label: 'Plazo que se supone cuando el proveedor no tiene ninguno',
            help: 'Sirve para proponer la fecha prevista de un pedido. Déjalo en 0 para no proponer nada.',
          },
          requireSupplierTaxId: {
            label: 'Exigir CIF/NIF en todos los proveedores',
            help: 'Déjalo desactivado si dais de alta a la ferretería de la esquina con un teléfono.',
          },
          alertRoles: {
            label: 'Quién se entera de que un material baja del mínimo',
            help: 'Solo hace algo con el módulo de almacén instalado.',
          },
          planAlertRoles: {
            label: 'Quién se entera de que al plan le faltará algo',
            help: 'Solo hace algo con el módulo de planificación S&OP instalado. Es una lista aparte de la de arriba: aquella avisa de lo que ya se ha agotado, y esta de lo que se agotará — que es lo único que todavía se puede resolver comprando a tiempo. Vacío es nadie.',
          },
          contractAlertRoles: {
            label: 'Quién se entera de que un contrato con un proveedor se acaba',
            help: 'Llega un aviso cuando el contrato entra en su plazo de aviso —para renegociar a tiempo— y otro cuando termina. Se repasa cada mañana a las 7:35. Cambiar la fecha de fin, al renovarlo, vuelve a armar los dos. Vacío es nadie.',
          },
          requisitionApproverRoles: {
            label: 'Quién se entera de que hay una solicitud de compra por aprobar',
            help: 'Aprobar pide el permiso «Aprobar solicitudes de compra»; esto solo decide a quién se avisa. Vacío es nadie.',
          },
          requisitionThreshold: {
            label: 'Importe estimado a partir del cual una solicitud necesita también a dirección',
            help: 'Sin IVA. En 0, compras aprueba todas.',
          },
          requisitionDirectorRoles: {
            label: 'Quién se entera de que una solicitud por encima del tope necesita a dirección',
            help: 'La segunda aprobación pide el permiso «Aprobar solicitudes por encima del tope».',
          },
          lateAlertRoles: {
            label: 'Quién se entera de que un pedido no ha llegado',
            help: 'Los pedidos cuya fecha prevista ya ha pasado y que no han llegado del todo. Se repasa cada mañana a las 7:30 y llega un aviso con cuántos hay y cuál es el más viejo; se repite mientras sigan tarde, y deja de sonar cuando se reciben o se cancelan. Vacío es nadie.',
          },
        },
      },
    },
    errors: {
      fields: {
        CONTRACT_OTHER_SUPPLIER: 'Ese contrato es con otro proveedor',
        CONTRACT_NOT_IN_FORCE: 'Ese contrato no está vigente en la fecha del pedido',
        ENDS_BEFORE_START: 'No puede terminar antes de empezar',
        INVALID_DATE: 'Eso no es una fecha',
      },
    },
    audit: {
      actions: {
        purchasing: {
          supplier_created: 'Proveedor dado de alta',
          supplier_updated: 'Proveedor modificado',
          supplier_archived: 'Proveedor archivado',
          supplier_restored: 'Proveedor devuelto al fichero',
          contact_added: 'Contacto añadido',
          contact_updated: 'Contacto modificado',
          contact_removed: 'Contacto eliminado',
          contact_primary_set: 'Contacto principal cambiado',
          purchase_created: 'Compra creada',
          purchase_updated: 'Compra modificada',
          purchase_draft: 'Compra reabierta',
          purchase_sent: 'Compra enviada al proveedor',
          purchase_answered: 'Solicitud respondida por el proveedor',
          purchase_confirmed: 'Pedido confirmado por el proveedor',
          purchase_partial: 'Recepción parcial',
          purchase_received: 'Pedido recibido',
          purchase_closed: 'Solicitud cerrada',
          purchase_cancelled: 'Compra anulada',
          purchase_archived: 'Compra archivada',
          purchase_restored: 'Compra devuelta a las listas',
          goods_received: 'Mercancía recibida',
          request_converted: 'Solicitud convertida en pedido',
          purchase_repeated: 'Pedido repetido',
          requisition_created: 'Solicitud de compra enviada',
          requisition_updated: 'Solicitud de compra modificada',
          requisition_approved: 'Solicitud de compra aprobada',
          requisition_rejected: 'Solicitud de compra rechazada',
          requisition_cancelled: 'Solicitud de compra retirada',
          requisition_converted: 'Solicitud de compra convertida en documento',
          contract_created: 'Contrato con proveedor creado',
          contract_updated: 'Contrato con proveedor modificado',
          contract_archived: 'Contrato con proveedor archivado',
          contract_restored: 'Contrato con proveedor devuelto',
        },
      },
    },
  },

  ca: {
    purchasing: {
      title: 'Compres',
      tabs: { overview: 'Fitxa' },
      // Las tarjetas del asistente (backend/assistant.js): el borrador, la
      // solicitud de compra y la decisión sobre ella.
      assistant: {
        draft: {
          summary: {
            order: 'Obrir un esborrany de comanda amb {count} línies per a {supplier}',
            price_request: 'Obrir un esborrany de petició de preu amb {count} línies per a {supplier}',
          },
          action: {
            order: 'Obrir esborrany de comanda',
            price_request: 'Obrir esborrany de petició de preu',
          },
          where: 'Proveïdor',
          after: 'Queda en esborrany: no surt res fins que algú l’enviï',
          moreLines: 'I {count} línies més',
          outcome: 'Esborrany {code} obert',
        },
        request: {
          summary: 'Demanar a compres: {title} ({count} línies)',
          action: 'Demanar a compres',
          noLines: 'Encara no hi ha res a comprar: digues què i quant',
          noQuantity: 'Cada línia necessita una quantitat',
          supplierUnlinked: '«{supplier}» no és a la fitxa de proveïdors: va com a nota',
          after: 'Compres l’aprovarà o dirà per què no',
          afterHigh: 'Passa del límit: després de compres l’aprova també direcció',
          outcome: 'Sol·licitud {code} enviada a compres',
        },
        approveRequest: {
          summary: 'Aprovar la sol·licitud {code} · {title}',
          action: 'Aprovar sol·licitud de compra',
          outcome: 'Sol·licitud {code} aprovada',
          outcomeHigh: '{code} aprovada per compres: ara decideix direcció',
        },
        rejectRequest: {
          summary: 'Rebutjar la sol·licitud {code} · {title}',
          action: 'Rebutjar sol·licitud de compra',
          outcome: 'Sol·licitud {code} rebutjada',
        },
        decide: {
          where: 'Sol·licitud de compra',
          notFound: 'No és clar quina sol·licitud és: tria-la a Sol·licituds de compra',
        },
        field: {
          supplier: 'Proveïdor',
          title: 'Què és',
          neededBy: 'Per a quan',
          total: 'Base imposable',
          destination: 'On',
          reason: 'Per a què',
          estimate: 'Estimació',
          request: 'Sol·licitud',
          requestedBy: 'Demanada per',
          note: 'Nota',
        },
      },

      dashboard: {
        open: 'Comandes en curs',
        openHelp: 'Quantes comandes a proveïdor estan compromeses i no han arribat del tot.',
        committed: 'Compromès',
        committedHelp: 'El que sumen les comandes en curs, en euros: despesa ja compromesa que encara no ha entrat.',
        suppliers: 'Proveïdors actius',
        suppliersHelp: 'Quants proveïdors estan actius o marcats com a preferents.',
        pendingAmount: 'Pendent amb proveïdors',
        pendingAmountHelp: 'El que s\'ha rebut de proveïdors que encara no us han facturat, en euros.',
        contractsEnding: 'Contractes que caduquen',
        contractsEndingHelp: 'Els contractes amb proveïdors dins del seu termini d\'avís o acabats de caducar: els que cal renegociar.',
        requisitions: 'Sol·licituds per aprovar',
        requisitionsHelp: 'Les sol·licituds de compra que esperen la teva aprovació.',
        awaiting: 'Esperant mercaderia',
        awaitingHelp: 'Les comandes en curs més recents, amb el seu proveïdor. Estira la targeta i n\'hi caben més.',
      },
      actions: {
        newOrder: 'Nova comanda', newRequest: 'Demanar preu', newSupplier: 'Nou proveïdor',
        newDeliveryNote: 'Albarà sense comanda',
        edit: 'Editar', save: 'Desar', cancel: 'Cancel·lar', close: 'Tancar', remove: 'Eliminar',
        discard: 'Descartar',
        archive: 'Arxivar', restore: 'Retornar al fitxer', addLine: 'Afegir línia', pdf: 'Descarregar PDF',
        send: 'Marcar com a enviat', confirm: 'Confirmar', receive: 'Rebre mercaderia',
        convert: 'Convertir en comanda', cancelDoc: 'Anul·lar', reopen: 'Reobrir',
        markAnswered: 'Marcar com a resposta', markClosed: 'Tancar sol·licitud',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      purchases: {
        title: 'Compres',
        subtitle: 'El que heu demanat i el que esteu esperant',
        newTitle: 'Nova compra', detailTitle: 'Compra',
        deliveryNoteTitle: 'Albarà sense comanda',
        kind: { order: 'Comanda', price_request: 'Sol·licitud de preu' },
        status: {
          draft: 'Esborrany', sent: 'Enviat', answered: 'Resposta', confirmed: 'Confirmat',
          partial: 'Parcial', received: 'Rebut', closed: 'Tancada', cancelled: 'Anul·lat',
        },
        filters: {
          search: 'Cercar', searchPlaceholder: 'Número, proveïdor, referència…',
          kind: 'Tipus', allKinds: 'Tots', status: 'Estat', allStatuses: 'Tots',
          onlyOpen: 'Només oberts', onlyOverdue: 'Només amb retard',
          only: 'Només les compres de {name}', showAll: 'Veure-les totes',
          oneCustomer: 'aquest client', oneWork: 'aquesta obra',
        },
        table: {
          code: 'Número', supplier: 'Proveïdor', title: 'Concepte', expected: 'Prevista',
          lines: 'Línies', total: 'Total', received: 'Rebut', status: 'Estat',
        },
        kpi: {
          open: 'Comandes obertes', committed: 'Compromès',
          overdue: 'Amb retard', received: 'Rebudes',
        },
        empty: {
          none: 'Encara no hi ha compres',
          noneHint: 'Comença demanant preu a un proveïdor, o obre una comanda directament.',
          filtered: 'Sense resultats',
          filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
          noLines: 'Aquest document no té línies',
          noLinesHint: 'Afegeix què s\'ha de comprar abans d\'enviar-lo.',
        },
        overdueBy: 'Amb {days} dies de retard',
        lineOf: '{received} de {ordered}',
        receivedHint: 'Línies que han arribat senceres. L’import rebut el porta l’obra.',
        created: 'Compra creada', saved: 'Compra desada',
        statusChanged: 'Estat actualitzat', archived: 'Compra arxivada',
        restored: 'Compra retornada a les llistes',
        converted: 'Comanda {code} creada a partir de la sol·licitud',
        blockedSupplier: 'Aquest proveïdor està bloquejat',
        search: { subtitle: 'Veure el document' },
      },
      suppliers: {
        title: 'Proveïdors',
        subtitle: 'La fitxa de cada empresa a qui compreu',
        newTitle: 'Nou proveïdor', editTitle: 'Editar proveïdor', detailTitle: 'Fitxa de proveïdor',
        loading: 'Carregant…',
        status: {
          active: 'Actiu', preferred: 'Preferent', inactive: 'Inactiu', blocked: 'Bloquejat',
        },
        filters: {
          search: 'Cercar', searchPlaceholder: 'Nom, NIF, població…',
          status: 'Estat', allStatuses: 'Tots', category: 'Categoria', allCategories: 'Totes',
        },
        table: {
          name: 'Empresa', taxId: 'NIF', category: 'Categoria', city: 'Població',
          contact: 'Contacte', leadTime: 'Termini', ordered: 'Comprat', status: 'Estat',
        },
        kpi: {
          active: 'Actius', preferred: 'Preferents', total: 'Total',
          ordered: 'Comprat', invoiced: 'Facturat',
        },
        empty: {
          none: 'Encara no hi ha proveïdors',
          noneHint: 'Doneu d\'alta el primer: sense proveïdor no es pot demanar res.',
          filtered: 'Sense resultats',
          filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
          noPurchases: 'Encara no li heu comprat res',
        },
        details: 'Dades de contacte',
        terms: 'Condicions de compra',
        address: 'Adreça',
        activity: 'Activitat',
        lastPurchases: 'Últimes compres',
        created: 'Proveïdor donat d\'alta', saved: 'Proveïdor desat',
        archive: {
          title: 'Arxivar proveïdor',
          body: 'Arxivareu {name}.',
          keeps: 'No s\'esborra res: deixa d\'aparèixer a les llistes i el seu historial queda intacte.',
          done: 'Proveïdor arxivat',
          restored: 'Proveïdor retornat al fitxer',
          isArchived: 'Aquest proveïdor està arxivat: no surt a cap llista i no se li pot comprar. Retornar-lo el torna a posar al fitxer, però INACTIU — tornar-li a comprar es la segona decisió.',
          blocked: 'Té compres obertes: tanca-les o anul·la-les abans d\'arxivar-lo.',
        },
        search: { subtitle: 'Veure la seva fitxa' },
      },
      related: {
        purchases: 'Compres i recepcions',
        subcontracts: 'Subcontractes',
        forCustomer: 'Compres',
      },
      spendType: {
        material: 'Material', plant: 'Lloguer de maquinària',
        subcontract: 'Subcontracta', service: 'Servei', other: 'Altres',
      },
      fields: {
        name: 'Raó social', namePlaceholder: 'Nom complet de l\'empresa',
        shortName: 'Nom curt', shortNameHint: 'El que feu servir a les llistes',
        taxId: 'NIF', status: 'Estat', category: 'Categoria',
        categoryPlaceholder: 'Ex.: Ferreteria, transport, acer',
        tags: 'Etiquetes', rating: 'Valoració', noRating: 'Sense valorar',
        email: 'Correu', phone: 'Telèfon', website: 'Web',
        address: 'Carrer', postalCode: 'CP', city: 'Població', country: 'País',
        paymentTerms: 'Forma de pagament', paymentMethod: 'Mitjà de pagament', currency: 'Moneda',
        discount: 'Descompte', discountHint: 'El que us fan sobre tarifa, en %',
        leadTime: 'Termini de lliurament', leadTimeHint: 'Dies des de la comanda. 0 si no ho sabeu',
        minOrder: 'Comanda mínima', iban: 'IBAN',
        supplierRef: 'El nostre codi amb ells',
        supplierRefHint: 'El número de compte que fan servir ells per a nosaltres; surt a les seves factures',
        notes: 'Notes', createdAt: 'Alta',
        contactName: 'Nom', contactPosition: 'Càrrec',
        contactPositionPlaceholder: 'Ex.: Comercial de zona',
        supplier: 'Proveïdor', customer: 'Client',
        customerHint: 'Si això es compra per a un client concret',
        customerPlaceholder: 'Nom del client',
        work: 'Obra', workPlaceholder: 'Cercar una obra',
        workHint: "Per a quina obra es compra. Amb això, l'albarà entra sol al cost de l'obra en lloc de teclejar-lo.",
        package: 'Paquet', packagePlaceholder: 'Cercar un paquet',
        packageHint: 'Contra quin paquet de contractació. És el que fa que aquesta comanda descompti del disponible correcte.',
        spendType: 'Classe de despesa',
        spendTypeHint: 'Separa material de lloguer i de subcontracta, que és el que es mira quan una partida es desvia.',
        kind: 'Tipus de document', concept: 'Concepte',
        conceptPlaceholder: 'Per a què és aquesta compra, en poques paraules',
        reference: 'Referència del proveïdor',
        referenceHint: 'El seu pressupost, la seva proforma: el que us han passat ells',
        issueDate: 'Data', expectedDate: 'Data prevista',
        receivedAt: 'Data de lliurament',
        receivedAtHint: 'El dia que va arribar el material. El cost és d’aquell dia, no d’avui.',
        deliveryAddress: 'Adreça de lliurament',
        deliveryHint: 'Deixa-la buida si va a la vostra adreça de sempre',
        invoiceRef: 'Factura del proveïdor',
        notesForSupplier: 'Notes per al proveïdor',
        notesForSupplierHint: 'Això s\'imprimeix al document que reben',
        internalNotes: 'Notes internes', internalNotesHint: 'Això no surt al document',
      },
      lines: {
        title: 'Línies', ref: 'Ref.', description: 'Descripció',
        quantity: 'Quant.', unit: 'Unitat', unitPrice: 'Preu', discount: 'Dte.',
        tax: 'IVA', total: 'Total', received: 'Rebut',
        descriptionPlaceholder: 'Què es demana',
        subtotal: 'Base imposable', discountTotal: 'Descomptes', taxTotal: 'IVA', grandTotal: 'Total',
      },
      contacts: {
        title: 'Persones de contacte',
        add: 'Afegir contacte', addTitle: 'Afegir contacte', editTitle: 'Editar contacte',
        none: 'Encara no hi ha contactes',
        noneHint: 'La persona a qui truqueu quan passa alguna cosa amb una comanda.',
        primary: 'Principal', makePrimary: 'Fer principal', markPrimary: 'Marcar com a contacte principal',
        primaryHint: 'És el nom que surt a les comandes. Només n\'hi pot haver un.',
        removeTitle: 'Eliminar contacte', removeBody: 'Voleu eliminar {name} d\'aquest proveïdor?',
        removed: 'Contacte eliminat', saved: 'Contacte desat',
      },
      files: {
        title: 'Papers de la comanda',
        add: 'Pujar un document', uploading: 'Pujant…', uploaded: 'Document pujat',
        whatIsIt: 'Què és?',
        kind: { delivery_note: 'Albarà', invoice: 'Factura', other: 'Altre' },
        dropTitle: 'Treure el document',
        dropConfirm: '«{name}» deixa d’estar en aquesta comanda.',
        none: {
          title: 'Sense papers',
          subtitle: 'Puja aquí l’albarà de cada lliurament i la factura quan arribi.',
        },
      },
      receive: {
        title: 'Rebre mercaderia',
        hint: 'Apunta el que ha arribat en AQUEST albarà, no el total acumulat.',
        pending: 'Pendent', arrived: 'Arriba ara', all: 'Tot el que falta',
        deliveryNote: 'L’albarà',
        deliveryNoteHint: 'Puja’l ara, que és quan el tens a la mà.',
        pickFile: 'Triar el fitxer…',
        date: 'Data de l\'albarà', note: 'Nota de l\'albarà',
        notePlaceholder: 'Ex.: dues caixes bonyegades, acceptades',
        done: 'Recepció registrada',
        complete: 'Comanda completa', partial: 'Recepció parcial',
        nothing: 'No has marcat res com a rebut',
      },
      requisitions: {
        title: 'Sol·licituds',
        subtitle: 'El que algú necessita que es compri, fins que compres ho aprova i ho demana',
        new: 'Nova sol·licitud',
        newTitle: 'Nova sol·licitud de compra',
        detailTitle: 'Sol·licitud de compra',
        newHint: 'Explica què necessites: compres ho revisa i t\'avisa.',
        search: 'Cercar per número, què es demana o qui ho demana…',
        send: 'Enviar la sol·licitud',
        sent: 'Sol·licitud {code} enviada a compres',
        saved: 'Sol·licitud desada',
        approved: 'Sol·licitud aprovada',
        rejected: 'Sol·licitud rebutjada',
        cancelled: 'Sol·licitud retirada',
        converted: 'Creat {code}: revisa preus i proveïdor abans d\'enviar-lo',
        cancelTitle: 'Retirar la sol·licitud?',
        cancelBody: 'La sol·licitud {code} deixa d\'estar pendent. Queda a l\'historial.',
        scope: {
          toApprove: 'Per aprovar',
          mine: 'Les meves sol·licituds',
          all: 'Totes',
        },
        empty: {
          toApprove: 'No hi ha res esperant la teva aprovació.',
          mine: 'Encara no has demanat res.',
          mineHint: 'Quan necessitis que es compri alguna cosa, demana-ho aquí: compres ho veurà al moment.',
          all: 'No hi ha sol·licituds.',
        },
        table: {
          what: 'Què es demana',
          neededBy: 'Per a quan',
          estimate: 'Estimat',
          status: 'Estat',
        },
        status: {
          pending: 'Pendent',
          awaiting_direction: 'Falta direcció',
          approved: 'Aprovada',
          rejected: 'Rebutjada',
          converted: 'Demanada',
          cancelled: 'Retirada',
        },
        statusHint: {
          pending: 'Esperant que compres la revisi.',
          awaiting_direction: 'Compres l\'ha aprovada; pel seu import falta el vistiplau de direcció.',
          approved: 'Aprovada: compres la convertirà en petició de preu o en comanda.',
          rejected: 'No es comprarà. El motiu és a l\'historial.',
          converted: 'Ja està en marxa al document',
          cancelled: 'Es va retirar abans de comprar-se.',
        },
        fields: {
          title: 'Què necessites?',
          titlePlaceholder: 'Radial de 230 mm per al taller',
          reason: 'Per a què',
          reasonHint: 'El primer que preguntarà compres: explica-ho aquí i t\'estalvies la trucada.',
          neededBy: 'Per a quan',
          destination: 'On va',
          destinationHint: 'Una màquina, una furgoneta, un taller…',
          destinationPlaceholder: 'Taller 2',
          supplier: 'Proveïdor suggerit',
          supplierHint: 'Si saps on el tenen. Compres decideix.',
        },
        lines: {
          title: 'El que es demana',
          description: 'Descripció',
          descriptionPlaceholder: 'Radial Makita 230 mm',
          materialPlaceholder: 'Cerca al magatzem o escriu què cal',
          locationPlaceholder: 'Ubicació de destinació (opcional)',
          inStock: 'Al magatzem: {n} {unit}',
          quantity: 'Quantitat',
          unit: 'Unitat',
          estimate: 'Preu aprox.',
          total: 'Import estimat',
          totalHint: 'Orientatiu: el preu real el posa compres.',
        },
        actions: {
          approve: 'Aprovar',
          approveHigh: 'Aprovar (direcció)',
          reject: 'Rebutjar',
          convert: 'Convertir en comanda…',
          cancel: 'Retirar',
        },
        reject: {
          title: 'Rebutjar la sol·licitud',
          reason: 'Motiu',
          placeholder: 'Ja n\'hi ha una al magatzem de la nau 1…',
        },
        convert: {
          title: 'Comprar el que s\'ha sol·licitat',
          kind: 'Què es crea',
          order: 'Comanda',
          priceRequest: 'Petició de preu',
          hint: 'Es crea en esborrany amb aquestes línies i els preus estimats: els revises abans d\'enviar-lo.',
          go: 'Crear',
        },
        history: {
          title: 'Historial',
          requested: 'la va sol·licitar',
          edited: 'la va modificar',
          approved_purchasing: 'la va aprovar (compres)',
          approved: 'la va aprovar',
          rejected: 'la va rebutjar',
          cancelled: 'la va retirar',
          converted: 'la va convertir en',
        },
      },
      contracts: {
        title: 'Contractes',
        subtitle: 'El que s\'ha pactat amb cada proveïdor: preus, import compromès i vigència',
        new: 'Nou contracte',
        newTitle: 'Nou contracte',
        detailTitle: 'Contracte',
        loading: 'Carregant…',
        created: 'Contracte creat',
        saved: 'Contracte desat',
        fields: {
          supplier: 'Proveïdor',
          title: 'Nom del contracte',
          titlePlaceholder: 'Acord marc 2026, tarifa de canonada…',
          reference: 'El seu número de contracte',
          referenceHint: 'El que figura al seu paper. Surt imprès a cada comanda feta amb aquest contracte.',
          startsOn: 'Vigent des de',
          endsOn: 'Vigent fins a',
          noticeDays: 'Avisar amb (dies)',
          noticeHint: 'Quants dies abans del final avisa l\'ERP, per renegociar a temps.',
          committedAmount: 'Import compromès',
          committedHint: 'El que us heu compromès a comprar, sense IVA. Deixa-ho a 0 si només es van pactar preus.',
          notes: 'Notes internes',
        },
        state: {
          upcoming: 'Encara no vigent',
          active: 'Vigent',
          expiring: 'Caduca aviat',
          expired: 'Caducat',
        },
        table: {
          contract: 'Contracte',
          supplier: 'Proveïdor',
          validity: 'Vigència',
          consumed: 'Consumit',
          state: 'Estat',
        },
        kpi: {
          inForce: 'Vigents',
          expiring: 'Caduquen aviat',
          expired: 'Caducats',
          committed: 'Compromès vigent',
        },
        filters: {
          search: 'Cercar per nom, número o proveïdor…',
          show: 'Mostrar',
          all: 'Tots',
          inForce: 'Només vigents',
          archived: 'Arxivats',
        },
        empty: {
          none: 'Encara no hi ha contractes',
          noneHint: 'Dona d\'alta el que s\'ha pactat amb un proveïdor: els seus preus s\'aplicaran sols en fer-li una comanda.',
          filtered: 'Cap contracte no coincideix',
          filteredHint: 'Prova de treure el filtre o de cercar una altra cosa.',
        },
        usage: {
          title: 'Consum',
          of: '{consumed} de {committed}',
          remaining: 'Queden {amount}',
          over: 'Superat en {amount}',
          noCommitment: 'Sense import compromès: el contracte només fixa preus.',
          orders: '{n} comanda(es) amb aquest contracte',
          consumedHint: 'Comandes enviades o posteriors, sense IVA. Els esborranys i les anul·lades no compten.',
        },
        days: {
          left: 'Queden {n} dies',
          today: 'S\'acaba avui',
          ago: 'Es va acabar fa {n} dies',
          startsIn: 'Comença d\'aquí a {n} dies',
          until: 'Fins al {date}',
          from: 'Des del {date}',
        },
        prices: {
          title: 'Preus pactats',
          hint: 'Una línia de comanda amb la mateixa referència —o, sense referència, la mateixa descripció— porta aquest preu mentre el contracte sigui vigent.',
          add: 'Afegir preu',
          fromHistory: 'Des de compres anteriors',
          none: 'Sense preus pactats',
          noneHint: 'Afegeix les referències amb el seu preu. Un contracte sense preus només porta el compte de l\'import.',
          ref: 'Ref.',
          description: 'Descripció',
          unit: 'Unitat',
          unitPrice: 'Preu',
          discount: 'Dte. %',
          count: '{n} preu(s) pactat(s)',
        },
        files: {
          title: 'Contracte signat',
          add: 'Pujar document',
          uploading: 'Pujant…',
          none: 'Encara no s\'ha pujat el contracte signat',
          noneHint: 'Puja\'l en PDF: és el que es busca quan un preu no quadra amb la factura.',
          kind: {
            signed: 'Signat',
            other: 'Altre',
          },
          whatIsIt: 'Què és',
          uploaded: 'Document pujat',
          dropTitle: 'Treure document',
          dropConfirm: 'Vols treure «{name}» del contracte?',
          saveFirst: 'Desa el contracte per poder pujar el document signat.',
        },
        purchases: {
          title: 'Comandes fetes amb aquest contracte',
          none: 'Encara no s\'ha demanat res amb aquest contracte.',
        },
        archive: {
          title: 'Arxivar contracte',
          body: 'El contracte {code} deixa d\'oferir-se en fer comandes. Les comandes fetes amb ell el continuen mostrant.',
          done: 'Contracte arxivat',
          restored: 'Contracte retornat',
          isArchived: 'Aquest contracte està arxivat: no s\'ofereix en fer comandes.',
        },
        inPurchase: {
          field: 'Contracte',
          none: 'Sense contracte',
          hint: 'Els seus preus pactats s\'apliquen sols a les línies que coincideixen.',
          agreed: 'Pactat',
          agreedTitle: 'Preu del contracte {code}',
          remaining: 'Queden {amount} de {committed} compromesos.',
          exceeds: 'Aquesta comanda supera en {amount} el que queda del contracte.',
          endsOn: 'Vigent fins al {date}.',
          pickerHeading: 'Preus pactats · {code}',
          open: 'Veure contracte',
        },
        supplier: {
          title: 'Contractes',
          none: 'Sense contractes amb aquest proveïdor.',
          add: 'Nou contracte',
        },
      },
      history: {
        open: 'Des de compres anteriors',
        title: 'El que ja hem comprat',
        search: 'Cercar per descripció o referència…',
        loading: 'Cercant…',
        fromSupplier: 'Comprat a {name}',
        fromOthers: 'Comprat a altres proveïdors',
        all: 'Comprat abans',
        noneFromSupplier: 'A aquest proveïdor encara no li hem comprat res semblant.',
        none: 'No hi ha res que hi coincideixi.',
        times: '{n} vegades',
        last: 'darrer {code}, {date}',
        added: '{n} afegida(es) · darrera: {name}',
        done: 'Fet',
        repeat: 'Repetir comanda',
        repeated: 'Esborrany {code} creat amb les mateixes línies',
      },
      send: {
        action: 'Enviar',
        toSupplier: 'Enviar al proveïdor',
        again: 'Tornar a enviar',
      },
      // Lo que «Enviar por email» pone de asunto en el redactor de la plataforma
      // (use-cases/PurchaseMail.js → subjectKey).
      mail: {
        subject: {
          order: 'Comanda {code}',
          request: 'Petició de preu {code}',
        },
      },
      cancel: {
        title: 'Anul·lar document',
        body: 'Anul·lareu {code}.',
        reason: 'Motiu', reasonHint: 'Queda escrit: és el que evita que algú ho torni a demanar d\'aquí a un mes.',
        reasonPlaceholder: 'Ex.: el proveïdor no pot servir en data',
        done: 'Document anul·lat',
      },
      document: {
        order: 'Comanda de compra', priceRequest: 'Sol·licitud de preu',
        date: 'Data', expected: 'Necessari per a', supplier: 'Proveïdor',
        ourRef: 'La nostra referència', yourRef: 'La vostra referència', deliverTo: 'Lliurar a',
        contract: 'Contracte',
        taxId: 'NIF', ref: 'Ref.', description: 'Descripció', quantity: 'Quant.',
        unitPrice: 'Preu', discount: 'Dte.', total: 'Total', subtotal: 'Base imposable',
        tax: 'IVA', grandTotal: 'Total', paymentTerms: 'Forma de pagament', notes: 'Notes',
        priceRequestNote: 'Això és una sol·licitud de preu. No és una comanda.',
      },
      notifications: {
        stockAlert: 'Material per sota del mínim',
        lateOrder: 'Una comanda que no ha arribat',
        contractExpiry: 'Un contracte amb un proveïdor que s\'acaba',
        contract: {
          notice: {
            title: 'El contracte {code} caduca aviat',
            body: '«{title}» amb {supplier} s\'acaba el {endsOn}: queden {days} dies per renegociar-lo.',
          },
          expired: {
            title: 'El contracte {code} ha caducat',
            body: '«{title}» amb {supplier} es va acabar el {endsOn}. Les comandes noves ja no porten els seus preus.',
          },
        },
        requisitionCategory: 'Sol·licituds de compra',
        requisition: {
          toApprove: {
            title: 'Sol·licitud {code} per aprovar',
            body: '{who} demana: {title}.',
          },
          toDirection: {
            title: 'Sol·licitud {code}: falta direcció',
            body: '{title} ({who}) supera el límit i necessita la teva aprovació.',
          },
          approved: {
            title: 'La teva sol·licitud {code} està aprovada',
            body: '{title}: compres la tramitarà.',
          },
          rejected: {
            title: 'La teva sol·licitud {code} no es compra',
            body: '{title}. Motiu: {extra}',
          },
          converted: {
            title: 'La teva sol·licitud {code} està en marxa',
            body: '{title}: ja és al document {extra}.',
          },
        },
        lateOrders: {
          title: 'Hi ha comandes que no han arribat',
          body: '{count} comanda/es han passat la seva data. La més antiga, {code} de {supplier}, fa {days} dia/es.',
        },
        lowStock: {
          title: 'Material per sota del mínim',
          body: '{material}: en queden {stock} i el mínim són {minimum}.',
        },
        planAlert: 'El pla avisa que faltarà alguna cosa',
        planRisk: {
          material: 'Faltarà material',
          lead_time: 'El termini del proveïdor ja no hi arriba',
          supplier: 'Risc amb un proveïdor',
          body: '{subject} — {period}, segons el pla {plan}.',
        },
      },
    },

    modules: { purchasing: 'Compres' },
    assistant: {
      openers: {
        purchasing: {
          'whats-late': 'Què estem esperant que no ha arribat?',
          'spend-this-quarter': 'Quant hem comprat aquest trimestre, i a qui?',
          'draft-an-order': 'Prepara\'m un esborrany de comanda',
          'ask-to-buy': 'Demana a compres una cosa que necessito',
          'requests-waiting': 'Quines sol·licituds de compra m’esperen?',
        },
      },
    },
    permissions: {
      purchasing: {
        read: 'Veure proveïdors i compres',
        write: 'Crear i editar compres',
        send: 'Enviar una comanda a un proveïdor',
        receive: 'Donar mercaderia per rebuda',
        suppliers: 'Mantenir la fitxa de proveïdors',
        request: 'Demanar que es compri alguna cosa (sense poder comprar)',
        approve: 'Aprovar sol·licituds de compra',
        approve_high: 'Aprovar sol·licituds per sobre del límit',
        award: 'Adjudicar comparatius',
      },
    },
    settings: {
      mail: {
      },
      ns: {
        purchasing: {
          title: 'Compres',
          subtitle: 'Amb quines dades neixen un proveïdor i una compra nous',
          defaultCountry: { label: 'País amb què neix un proveïdor nou' },
          defaultCurrency: {
            label: 'Moneda en què us factura un proveïdor nou',
            help: 'ISO 4217, tres lletres. Després es pot canviar proveïdor a proveïdor.',
          },
          defaultPaymentTerms: { label: 'Forma de pagament amb què neix un proveïdor nou' },
          defaultTaxRate: {
            label: 'IVA amb què neix una compra nova',
            help: 'El que hereta cada línia si no diu una altra cosa. 0, 4, 10 o 21.',
          },
          defaultLeadTimeDays: {
            label: 'Termini que se suposa quan el proveïdor no en té cap',
            help: 'Serveix per proposar la data prevista d\'una comanda. Deixa\'l a 0 per no proposar res.',
          },
          requireSupplierTaxId: {
            label: 'Exigir NIF a tots els proveïdors',
            help: 'Deixa-ho desactivat si doneu d\'alta la ferreteria del costat amb un telèfon.',
          },
          alertRoles: {
            label: 'Qui s\'assabenta que un material baixa del mínim',
            help: 'Només fa alguna cosa amb el mòdul de magatzem instal·lat.',
          },
          planAlertRoles: {
            label: 'Qui s\'assabenta que al pla li faltarà alguna cosa',
            help: 'Només fa alguna cosa amb el mòdul de planificació S&OP instal·lat. És una llista a part de la de dalt: aquella avisa del que ja s\'ha exhaurit, i aquesta del que s\'exhaurirà — que és l\'únic que encara es pot resoldre comprant a temps. Buit vol dir ningú.',
          },
          contractAlertRoles: {
            label: 'Qui s\'assabenta que un contracte amb un proveïdor s\'acaba',
            help: 'Arriba un avís quan el contracte entra en el seu termini d\'avís —per renegociar a temps— i un altre quan s\'acaba. Es repassa cada matí a les 7:35. Canviar la data de fi, en renovar-lo, torna a armar tots dos. Buit és ningú.',
          },
          requisitionApproverRoles: {
            label: 'Qui s\'assabenta que hi ha una sol·licitud de compra per aprovar',
            help: 'Aprovar demana el permís «Aprovar sol·licituds de compra»; això només decideix a qui s\'avisa. Buit és ningú.',
          },
          requisitionThreshold: {
            label: 'Import estimat a partir del qual una sol·licitud també necessita direcció',
            help: 'Sense IVA. A 0, compres les aprova totes.',
          },
          requisitionDirectorRoles: {
            label: 'Qui s\'assabenta que una sol·licitud per sobre del límit necessita direcció',
            help: 'La segona aprovació demana el permís «Aprovar sol·licituds per sobre del límit».',
          },
          lateAlertRoles: {
            label: 'Qui s\'assabenta que una comanda no ha arribat',
            help: 'Les comandes amb la data prevista passada i que no han arribat del tot. Es repassa cada matí a les 7:30 i arriba un avís amb quantes n\'hi ha i quina és la més antiga; es repeteix mentre segueixin tard, i deixa de sonar quan es reben o es cancel·len. Buit és ningú.',
          },
        },
      },
    },
    errors: {
      fields: {
        CONTRACT_OTHER_SUPPLIER: 'Aquest contracte és amb un altre proveïdor',
        CONTRACT_NOT_IN_FORCE: 'Aquest contracte no és vigent en la data de la comanda',
        ENDS_BEFORE_START: 'No pot acabar abans de començar',
        INVALID_DATE: 'Això no és una data',
      },
    },
    audit: {
      actions: {
        purchasing: {
          supplier_created: 'Proveïdor donat d\'alta',
          supplier_updated: 'Proveïdor modificat',
          supplier_archived: 'Proveïdor arxivat',
          supplier_restored: 'Proveïdor retornat al fitxer',
          contact_added: 'Contacte afegit',
          contact_updated: 'Contacte modificat',
          contact_removed: 'Contacte eliminat',
          contact_primary_set: 'Contacte principal canviat',
          purchase_created: 'Compra creada',
          purchase_updated: 'Compra modificada',
          purchase_draft: 'Compra reoberta',
          purchase_sent: 'Compra enviada al proveïdor',
          purchase_answered: 'Sol·licitud resposta pel proveïdor',
          purchase_confirmed: 'Comanda confirmada pel proveïdor',
          purchase_partial: 'Recepció parcial',
          purchase_received: 'Comanda rebuda',
          purchase_closed: 'Sol·licitud tancada',
          purchase_cancelled: 'Compra anul·lada',
          purchase_archived: 'Compra arxivada',
          purchase_restored: 'Compra retornada a les llistes',
          goods_received: 'Mercaderia rebuda',
          request_converted: 'Sol·licitud convertida en comanda',
          purchase_repeated: 'Comanda repetida',
          requisition_created: 'Sol·licitud de compra enviada',
          requisition_updated: 'Sol·licitud de compra modificada',
          requisition_approved: 'Sol·licitud de compra aprovada',
          requisition_rejected: 'Sol·licitud de compra rebutjada',
          requisition_cancelled: 'Sol·licitud de compra retirada',
          requisition_converted: 'Sol·licitud de compra convertida en document',
          contract_created: 'Contracte amb proveïdor creat',
          contract_updated: 'Contracte amb proveïdor modificat',
          contract_archived: 'Contracte amb proveïdor arxivat',
          contract_restored: 'Contracte amb proveïdor retornat',
        },
      },
    },
  },

  en: {
    purchasing: {
      title: 'Purchasing',
      tabs: { overview: 'Details' },
      // Las tarjetas del asistente (backend/assistant.js): el borrador, la
      // solicitud de compra y la decisión sobre ella.
      assistant: {
        draft: {
          summary: {
            order: 'Open a draft order with {count} lines for {supplier}',
            price_request: 'Open a draft price request with {count} lines for {supplier}',
          },
          action: {
            order: 'Open a draft order',
            price_request: 'Open a draft price request',
          },
          where: 'Supplier',
          after: 'It stays a draft: nothing is sent until somebody sends it',
          moreLines: 'And {count} more lines',
          outcome: 'Draft {code} opened',
        },
        request: {
          summary: 'Ask purchasing for: {title} ({count} lines)',
          action: 'Ask purchasing',
          noLines: 'Nothing to buy yet: say what and how many',
          noQuantity: 'Every line needs a quantity',
          supplierUnlinked: '«{supplier}» is not in the supplier file: it goes as a note',
          after: 'Purchasing will approve it or say why not',
          afterHigh: 'Above the ceiling: after purchasing, management approves it too',
          outcome: 'Request {code} sent to purchasing',
        },
        approveRequest: {
          summary: 'Approve request {code} · {title}',
          action: 'Approve purchase request',
          outcome: 'Request {code} approved',
          outcomeHigh: '{code} approved by purchasing: now management decides',
        },
        rejectRequest: {
          summary: 'Reject request {code} · {title}',
          action: 'Reject purchase request',
          outcome: 'Request {code} rejected',
        },
        decide: {
          where: 'Purchase request',
          notFound: 'Which request is not clear: pick it in Purchase requests',
        },
        field: {
          supplier: 'Supplier',
          title: 'What for',
          neededBy: 'Needed by',
          total: 'Before tax',
          destination: 'Where to',
          reason: 'What it is for',
          estimate: 'Estimate',
          request: 'Request',
          requestedBy: 'Asked by',
          note: 'Note',
        },
      },

      dashboard: {
        open: 'Orders in progress',
        openHelp: 'How many purchase orders are committed and have not fully arrived.',
        committed: 'Committed',
        committedHelp: 'What the orders in progress add up to, in euros: money spent that has not come in yet.',
        suppliers: 'Active suppliers',
        suppliersHelp: 'How many suppliers are active or marked as preferred.',
        pendingAmount: 'Owed to suppliers',
        pendingAmountHelp: 'What has been received from suppliers who have not invoiced you yet, in euros.',
        contractsEnding: 'Contracts ending',
        contractsEndingHelp: 'Supplier contracts inside their notice window or just expired: the ones to renegotiate.',
        requisitions: 'Requisitions to approve',
        requisitionsHelp: 'The purchase requisitions waiting for your approval.',
        awaiting: 'Awaiting goods',
        awaitingHelp: 'The most recent orders in progress, with their supplier. Stretch the card and more fit.',
      },
      actions: {
        newOrder: 'New order', newRequest: 'Ask for a price', newSupplier: 'New supplier',
        newDeliveryNote: 'Delivery note without an order',
        edit: 'Edit', save: 'Save', cancel: 'Cancel', close: 'Close', remove: 'Remove',
        discard: 'Discard',
        archive: 'Archive', restore: 'Bring back', addLine: 'Add line', pdf: 'Download PDF',
        send: 'Mark as sent', confirm: 'Confirm', receive: 'Receive goods',
        convert: 'Turn into an order', cancelDoc: 'Cancel document', reopen: 'Reopen',
        markAnswered: 'Mark as answered', markClosed: 'Close request',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      purchases: {
        title: 'Purchases',
        subtitle: 'What you have ordered and what you are waiting for',
        newTitle: 'New purchase', detailTitle: 'Purchase',
        deliveryNoteTitle: 'Delivery note without an order',
        kind: { order: 'Order', price_request: 'Price request' },
        status: {
          draft: 'Draft', sent: 'Sent', answered: 'Answered', confirmed: 'Confirmed',
          partial: 'Partial', received: 'Received', closed: 'Closed', cancelled: 'Cancelled',
        },
        filters: {
          search: 'Search', searchPlaceholder: 'Number, supplier, reference…',
          kind: 'Kind', allKinds: 'All', status: 'Status', allStatuses: 'All',
          onlyOpen: 'Open only', onlyOverdue: 'Late only',
          only: 'Only purchases for {name}', showAll: 'Show all',
          oneCustomer: 'this customer', oneWork: 'this job',
        },
        table: {
          code: 'Number', supplier: 'Supplier', title: 'Subject', expected: 'Expected',
          lines: 'Lines', total: 'Total', received: 'Received', status: 'Status',
        },
        kpi: {
          open: 'Open orders', committed: 'Committed',
          overdue: 'Late', received: 'Received',
        },
        empty: {
          none: 'No purchases yet',
          noneHint: 'Start by asking a supplier for a price, or open an order straight away.',
          filtered: 'Nothing found',
          filteredHint: 'Try clearing a filter or searching for something else.',
          noLines: 'This document has no lines',
          noLinesHint: 'Add what has to be bought before sending it.',
        },
        overdueBy: '{days} days late',
        lineOf: '{received} of {ordered}',
        receivedHint: 'Lines fully delivered. The amount received is kept by the work.',
        created: 'Purchase created', saved: 'Purchase saved',
        statusChanged: 'Status updated', archived: 'Purchase archived',
        restored: 'Purchase brought back',
        converted: 'Order {code} created from the request',
        blockedSupplier: 'This supplier is blocked',
        search: { subtitle: 'Open the document' },
      },
      suppliers: {
        title: 'Suppliers',
        subtitle: 'The file on every company you buy from',
        newTitle: 'New supplier', editTitle: 'Edit supplier', detailTitle: 'Supplier file',
        loading: 'Loading…',
        status: {
          active: 'Active', preferred: 'Preferred', inactive: 'Inactive', blocked: 'Blocked',
        },
        filters: {
          search: 'Search', searchPlaceholder: 'Name, tax id, town…',
          status: 'Status', allStatuses: 'All', category: 'Category', allCategories: 'All',
        },
        table: {
          name: 'Company', taxId: 'Tax id', category: 'Category', city: 'Town',
          contact: 'Contact', leadTime: 'Lead time', ordered: 'Ordered', status: 'Status',
        },
        kpi: {
          active: 'Active', preferred: 'Preferred', total: 'Total',
          ordered: 'Ordered', invoiced: 'Invoiced',
        },
        empty: {
          none: 'No suppliers yet',
          noneHint: 'File the first one: nothing can be ordered without a supplier.',
          filtered: 'Nothing found',
          filteredHint: 'Try clearing a filter or searching for something else.',
          noPurchases: 'Nothing has been bought from them yet',
        },
        details: 'Contact details',
        terms: 'Buying terms',
        address: 'Address',
        activity: 'Activity',
        lastPurchases: 'Latest purchases',
        created: 'Supplier filed', saved: 'Supplier saved',
        archive: {
          title: 'Archive supplier',
          body: 'You are about to archive {name}.',
          keeps: 'Nothing is deleted: they drop off every list and their history stays intact.',
          done: 'Supplier archived',
          restored: 'Supplier brought back on file',
          isArchived: 'This supplier is archived: it is on no list and nothing can be bought from them. Bringing it back puts it on file again, but INACTIVE — buying from them again is the second decision.',
          blocked: 'They have open purchases: close or cancel those before archiving them.',
        },
        search: { subtitle: 'Open their file' },
      },
      related: {
        purchases: 'Purchases and receipts',
        subcontracts: 'Subcontracts',
        forCustomer: 'Purchases',
      },
      spendType: {
        material: 'Material', plant: 'Plant hire',
        subcontract: 'Subcontract', service: 'Service', other: 'Other',
      },
      fields: {
        name: 'Registered name', namePlaceholder: 'Full company name',
        shortName: 'Short name', shortNameHint: 'The one you use in lists',
        taxId: 'Tax id', status: 'Status', category: 'Category',
        categoryPlaceholder: 'e.g. Ironmonger, haulage, steel',
        tags: 'Tags', rating: 'Rating', noRating: 'Not rated',
        email: 'Email', phone: 'Phone', website: 'Website',
        address: 'Street', postalCode: 'Postcode', city: 'Town', country: 'Country',
        paymentTerms: 'Payment terms', paymentMethod: 'Payment method', currency: 'Currency',
        discount: 'Discount', discountHint: 'What they knock off list price, in %',
        leadTime: 'Lead time', leadTimeHint: 'Days from the order. 0 if you do not know',
        minOrder: 'Minimum order', iban: 'IBAN',
        supplierRef: 'Our account with them',
        supplierRefHint: 'The number they use for you; it is on their invoices',
        notes: 'Notes', createdAt: 'Filed',
        contactName: 'Name', contactPosition: 'Position',
        contactPositionPlaceholder: 'e.g. Area sales',
        supplier: 'Supplier', customer: 'Customer',
        customerHint: 'If this is being bought for a specific customer',
        customerPlaceholder: 'Customer name',
        work: 'Work', workPlaceholder: 'Find a work',
        workHint: 'Which work this is bought for. With it, the delivery note lands on the cost of that work instead of being typed in.',
        package: 'Package', packagePlaceholder: 'Find a package',
        packageHint: 'Which procurement package this goes against. It is what makes the order draw down the right balance: picking it shows what is left before you sign.',
        spendType: 'Kind of spend',
        spendTypeHint: 'Tells material from plant hire and subcontract, which is what gets looked at when an item goes over.',
        kind: 'Document kind', concept: 'Subject',
        conceptPlaceholder: 'What this purchase is for, in a few words',
        reference: 'Supplier reference',
        referenceHint: 'Their quotation, their proforma: what they sent you',
        issueDate: 'Date', expectedDate: 'Expected',
        receivedAt: 'Delivered on',
        receivedAtHint: 'The day the goods arrived. The cost belongs to that day, not to today.',
        deliveryAddress: 'Delivery address',
        deliveryHint: 'Leave empty if it goes to your usual address',
        invoiceRef: 'Supplier invoice',
        notesForSupplier: 'Notes for the supplier',
        notesForSupplierHint: 'This is printed on the document they receive',
        internalNotes: 'Internal notes', internalNotesHint: 'This does not appear on the document',
      },
      lines: {
        title: 'Lines', ref: 'Ref.', description: 'Description',
        quantity: 'Qty', unit: 'Unit', unitPrice: 'Price', discount: 'Disc.',
        tax: 'VAT', total: 'Total', received: 'Received',
        descriptionPlaceholder: 'What is being ordered',
        subtotal: 'Subtotal', discountTotal: 'Discounts', taxTotal: 'Tax', grandTotal: 'Total',
      },
      contacts: {
        title: 'People to call',
        add: 'Add contact', addTitle: 'Add contact', editTitle: 'Edit contact',
        none: 'No contacts yet',
        noneHint: 'The person you ring when something happens with an order.',
        primary: 'Primary', makePrimary: 'Make primary', markPrimary: 'Mark as the primary contact',
        primaryHint: 'This is the name that goes on the order. There can be only one.',
        removeTitle: 'Remove contact', removeBody: 'Remove {name} from this supplier?',
        removed: 'Contact removed', saved: 'Contact saved',
      },
      files: {
        title: 'Order paperwork',
        add: 'Upload a document', uploading: 'Uploading…', uploaded: 'Document uploaded',
        whatIsIt: 'What is it?',
        kind: { delivery_note: 'Delivery note', invoice: 'Invoice', other: 'Other' },
        dropTitle: 'Remove the document',
        dropConfirm: '"{name}" comes off this order.',
        none: {
          title: 'No paperwork',
          subtitle: 'Upload each delivery note here, and the invoice when it arrives.',
        },
      },
      receive: {
        title: 'Receive goods',
        hint: 'Record what arrived on THIS delivery note, not the running total.',
        pending: 'Outstanding', arrived: 'Arriving now', all: 'Everything outstanding',
        deliveryNote: 'The delivery note',
        deliveryNoteHint: 'Upload it now, while it is in your hand.',
        pickFile: 'Choose the file…',
        date: 'Delivery note date', note: 'Delivery note remark',
        notePlaceholder: 'e.g. two boxes dented, accepted',
        done: 'Delivery recorded',
        complete: 'Order complete', partial: 'Partial delivery',
        nothing: 'Nothing was marked as received',
      },
      requisitions: {
        title: 'Requisitions',
        subtitle: 'What somebody needs bought, until purchasing approves it and orders it',
        new: 'New requisition',
        newTitle: 'New purchase requisition',
        detailTitle: 'Purchase requisition',
        newHint: 'Say what you need: purchasing reviews it and lets you know.',
        search: 'Search by number, what is asked for or who asked…',
        send: 'Send requisition',
        sent: 'Requisition {code} sent to purchasing',
        saved: 'Requisition saved',
        approved: 'Requisition approved',
        rejected: 'Requisition rejected',
        cancelled: 'Requisition withdrawn',
        converted: '{code} created: check prices and supplier before sending it',
        cancelTitle: 'Withdraw the requisition?',
        cancelBody: 'Requisition {code} stops being pending. It stays in the history.',
        scope: {
          toApprove: 'To approve',
          mine: 'My requisitions',
          all: 'All',
        },
        empty: {
          toApprove: 'Nothing is waiting for your approval.',
          mine: 'You have not asked for anything yet.',
          mineHint: 'When you need something bought, ask here: purchasing sees it straight away.',
          all: 'No requisitions.',
        },
        table: {
          what: 'What is asked for',
          neededBy: 'Needed by',
          estimate: 'Estimate',
          status: 'Status',
        },
        status: {
          pending: 'Pending',
          awaiting_direction: 'Needs management',
          approved: 'Approved',
          rejected: 'Rejected',
          converted: 'Ordered',
          cancelled: 'Withdrawn',
        },
        statusHint: {
          pending: 'Waiting for purchasing to review it.',
          awaiting_direction: 'Purchasing approved it; for its amount, management still has to.',
          approved: 'Approved: purchasing will turn it into a price request or an order.',
          rejected: 'It will not be bought. The reason is in the history.',
          converted: 'Already under way in the document',
          cancelled: 'Withdrawn before it was bought.',
        },
        fields: {
          title: 'What do you need?',
          titlePlaceholder: '230 mm angle grinder for the workshop',
          reason: 'What for',
          reasonHint: 'The first thing purchasing will ask: say it here and save the call.',
          neededBy: 'Needed by',
          destination: 'Where it goes',
          destinationHint: 'A machine, a van, a workshop…',
          destinationPlaceholder: 'Workshop 2',
          supplier: 'Suggested supplier',
          supplierHint: 'If you know who has it. Purchasing decides.',
        },
        lines: {
          title: 'What is asked for',
          description: 'Description',
          descriptionPlaceholder: 'Makita 230 mm angle grinder',
          materialPlaceholder: 'Search the warehouse or type what is needed',
          locationPlaceholder: 'Destination location (optional)',
          inStock: 'In stock: {n} {unit}',
          quantity: 'Quantity',
          unit: 'Unit',
          estimate: 'Approx. price',
          total: 'Estimated amount',
          totalHint: 'A guide: purchasing sets the real price.',
        },
        actions: {
          approve: 'Approve',
          approveHigh: 'Approve (management)',
          reject: 'Reject',
          convert: 'Turn into an order…',
          cancel: 'Withdraw',
        },
        reject: {
          title: 'Reject the requisition',
          reason: 'Reason',
          placeholder: 'There is already one in the building 1 store…',
        },
        convert: {
          title: 'Buy what was asked for',
          kind: 'What is created',
          order: 'Order',
          priceRequest: 'Price request',
          hint: 'Created as a draft with these lines and the estimated prices: you check them before sending it.',
          go: 'Create',
        },
        history: {
          title: 'History',
          requested: 'asked for it',
          edited: 'changed it',
          approved_purchasing: 'approved it (purchasing)',
          approved: 'approved it',
          rejected: 'rejected it',
          cancelled: 'withdrew it',
          converted: 'turned it into',
        },
      },
      contracts: {
        title: 'Contracts',
        subtitle: 'What was agreed with each supplier: prices, committed amount and validity',
        new: 'New contract',
        newTitle: 'New contract',
        detailTitle: 'Contract',
        loading: 'Loading…',
        created: 'Contract created',
        saved: 'Contract saved',
        fields: {
          supplier: 'Supplier',
          title: 'Contract name',
          titlePlaceholder: 'Framework agreement 2026, pipe rates…',
          reference: 'Their contract number',
          referenceHint: 'The one on their paper. It is printed on every order placed under this contract.',
          startsOn: 'Valid from',
          endsOn: 'Valid until',
          noticeDays: 'Warn (days before)',
          noticeHint: 'How many days before the end the ERP warns you, to renegotiate in time.',
          committedAmount: 'Committed amount',
          committedHint: 'What you committed to buy, before VAT. Leave it at 0 if only prices were agreed.',
          notes: 'Internal notes',
        },
        state: {
          upcoming: 'Not in force yet',
          active: 'In force',
          expiring: 'Ending soon',
          expired: 'Expired',
        },
        table: {
          contract: 'Contract',
          supplier: 'Supplier',
          validity: 'Validity',
          consumed: 'Consumed',
          state: 'Status',
        },
        kpi: {
          inForce: 'In force',
          expiring: 'Ending soon',
          expired: 'Expired',
          committed: 'Committed in force',
        },
        filters: {
          search: 'Search by name, number or supplier…',
          show: 'Show',
          all: 'All',
          inForce: 'In force only',
          archived: 'Archived',
        },
        empty: {
          none: 'No contracts yet',
          noneHint: 'Record what you agreed with a supplier: its prices will apply by themselves when you order from them.',
          filtered: 'No contract matches',
          filteredHint: 'Try removing the filter or searching for something else.',
        },
        usage: {
          title: 'Consumption',
          of: '{consumed} of {committed}',
          remaining: '{amount} left',
          over: 'Exceeded by {amount}',
          noCommitment: 'No committed amount: the contract only sets prices.',
          orders: '{n} order(s) under this contract',
          consumedHint: 'Orders sent or later, before VAT. Drafts and cancelled orders do not count.',
        },
        days: {
          left: '{n} days left',
          today: 'Ends today',
          ago: 'Ended {n} days ago',
          startsIn: 'Starts in {n} days',
          until: 'Until {date}',
          from: 'From {date}',
        },
        prices: {
          title: 'Agreed prices',
          hint: 'An order line with the same reference —or, without one, the same description— carries this price while the contract is in force.',
          add: 'Add price',
          fromHistory: 'From previous purchases',
          none: 'No agreed prices',
          noneHint: 'Add the references with their price. A contract without prices only keeps track of the amount.',
          ref: 'Ref.',
          description: 'Description',
          unit: 'Unit',
          unitPrice: 'Price',
          discount: 'Disc. %',
          count: '{n} agreed price(s)',
        },
        files: {
          title: 'Signed contract',
          add: 'Upload document',
          uploading: 'Uploading…',
          none: 'The signed contract has not been uploaded yet',
          noneHint: 'Upload it as a PDF: it is what you look for when a price does not match the invoice.',
          kind: {
            signed: 'Signed',
            other: 'Other',
          },
          whatIsIt: 'What it is',
          uploaded: 'Document uploaded',
          dropTitle: 'Remove document',
          dropConfirm: 'Remove “{name}” from the contract?',
          saveFirst: 'Save the contract to be able to upload the signed document.',
        },
        purchases: {
          title: 'Orders placed under this contract',
          none: 'Nothing has been ordered under this contract yet.',
        },
        archive: {
          title: 'Archive contract',
          body: 'Contract {code} is no longer offered when placing orders. Orders placed under it keep showing it.',
          done: 'Contract archived',
          restored: 'Contract restored',
          isArchived: 'This contract is archived: it is not offered when placing orders.',
        },
        inPurchase: {
          field: 'Contract',
          none: 'No contract',
          hint: 'Its agreed prices apply by themselves to the lines that match.',
          agreed: 'Agreed',
          agreedTitle: 'Price from contract {code}',
          remaining: '{amount} left of {committed} committed.',
          exceeds: 'This order exceeds what is left of the contract by {amount}.',
          endsOn: 'In force until {date}.',
          pickerHeading: 'Agreed prices · {code}',
          open: 'Open contract',
        },
        supplier: {
          title: 'Contracts',
          none: 'No contracts with this supplier.',
          add: 'New contract',
        },
      },
      history: {
        open: 'From previous purchases',
        title: 'What we have bought before',
        search: 'Search by description or reference…',
        loading: 'Searching…',
        fromSupplier: 'Bought from {name}',
        fromOthers: 'Bought from other suppliers',
        all: 'Bought before',
        noneFromSupplier: 'We have not bought anything like this from this supplier yet.',
        none: 'Nothing matches.',
        times: '{n} times',
        last: 'last {code}, {date}',
        added: '{n} added · last: {name}',
        done: 'Done',
        repeat: 'Repeat order',
        repeated: 'Draft {code} created with the same lines',
      },
      send: {
        action: 'Send',
        toSupplier: 'Send to supplier',
        again: 'Send again',
      },
      // Lo que «Enviar por email» pone de asunto en el redactor de la plataforma
      // (use-cases/PurchaseMail.js → subjectKey).
      mail: {
        subject: {
          order: 'Purchase order {code}',
          request: 'Request for quotation {code}',
        },
      },
      cancel: {
        title: 'Cancel document',
        body: 'You are about to cancel {code}.',
        reason: 'Reason', reasonHint: 'It stays on the record: it is what stops somebody re-ordering it in a month.',
        reasonPlaceholder: 'e.g. the supplier cannot deliver on time',
        done: 'Document cancelled',
      },
      document: {
        order: 'Purchase order', priceRequest: 'Request for quotation',
        date: 'Date', expected: 'Required by', supplier: 'Supplier',
        ourRef: 'Our reference', yourRef: 'Your reference', deliverTo: 'Deliver to',
        contract: 'Contract',
        taxId: 'Tax id', ref: 'Ref.', description: 'Description', quantity: 'Qty',
        unitPrice: 'Price', discount: 'Disc.', total: 'Total', subtotal: 'Subtotal',
        tax: 'VAT', grandTotal: 'Total', paymentTerms: 'Payment terms', notes: 'Notes',
        priceRequestNote: 'This is a request for a price. It is not an order.',
      },
      notifications: {
        stockAlert: 'Material below its minimum',
        lateOrder: 'An order that has not arrived',
        contractExpiry: 'A supplier contract coming to its end',
        contract: {
          notice: {
            title: 'Contract {code} is ending soon',
            body: '“{title}” with {supplier} ends on {endsOn}: {days} days left to renegotiate it.',
          },
          expired: {
            title: 'Contract {code} has expired',
            body: '“{title}” with {supplier} ended on {endsOn}. New orders no longer carry its prices.',
          },
        },
        requisitionCategory: 'Purchase requisitions',
        requisition: {
          toApprove: {
            title: 'Requisition {code} to approve',
            body: '{who} asks for: {title}.',
          },
          toDirection: {
            title: 'Requisition {code}: needs management',
            body: '{title} ({who}) is above the ceiling and needs your approval.',
          },
          approved: {
            title: 'Your requisition {code} is approved',
            body: '{title}: purchasing will handle it.',
          },
          rejected: {
            title: 'Your requisition {code} will not be bought',
            body: '{title}. Reason: {extra}',
          },
          converted: {
            title: 'Your requisition {code} is under way',
            body: '{title}: it is now in document {extra}.',
          },
        },
        lateOrders: {
          title: 'Orders that have not arrived',
          body: '{count} order(s) are past their date. The oldest, {code} from {supplier}, is {days} day(s) late.',
        },
        lowStock: {
          title: 'Material below its minimum',
          body: '{material}: {stock} left, minimum is {minimum}.',
        },
        planAlert: 'The plan says something will be short',
        planRisk: {
          material: 'Material will run short',
          lead_time: 'The supplier lead time no longer reaches',
          supplier: 'Supplier risk',
          body: '{subject} — {period}, according to plan {plan}.',
        },
      },
    },

    modules: { purchasing: 'Purchasing' },
    assistant: {
      openers: {
        purchasing: {
          'whats-late': 'What are we still waiting for?',
          'spend-this-quarter': 'What have we spent this quarter, and with whom?',
          'draft-an-order': 'Open a draft order for something I need',
          'ask-to-buy': 'Ask purchasing for something I need',
          'requests-waiting': 'Which purchase requests are waiting for me?',
        },
      },
    },
    permissions: {
      purchasing: {
        read: 'See suppliers and purchases',
        write: 'Create and edit purchases',
        send: 'Send an order to a supplier',
        receive: 'Sign for goods received',
        suppliers: 'Maintain the supplier file',
        request: 'Ask for something to be bought (without buying)',
        approve: 'Approve purchase requisitions',
        approve_high: 'Approve requisitions above the ceiling',
        award: 'Award bid comparisons',
      },
    },
    settings: {
      mail: {
      },
      ns: {
        purchasing: {
          title: 'Purchasing',
          subtitle: 'What a new supplier and a new purchase start with',
          defaultCountry: { label: 'Country a new supplier starts in' },
          defaultCurrency: {
            label: 'Currency a new supplier bills you in',
            help: 'ISO 4217, three letters. It can be changed per supplier afterwards.',
          },
          defaultPaymentTerms: { label: 'Payment terms a new supplier starts with' },
          defaultTaxRate: {
            label: 'VAT rate a new purchase starts with',
            help: 'The rate each line inherits unless it says otherwise. 0, 4, 10 or 21.',
          },
          defaultLeadTimeDays: {
            label: 'Delivery time assumed when a supplier has none set',
            help: 'Used to suggest the date an order is expected. Leave at 0 to suggest nothing.',
          },
          requireSupplierTaxId: {
            label: 'Require a tax id on every supplier',
            help: 'Leave it off if the ironmonger round the corner is on file with a phone number.',
          },
          alertRoles: {
            label: 'Who hears about a material below its minimum',
            help: 'Only does anything with the inventory module installed.',
          },
          planAlertRoles: {
            label: 'Who hears that the plan will be short of something',
            help: 'Only does anything with the S&OP module installed. A separate list from the one above: that one warns about what has already run out, this one about what will — which is the only one that can still be solved by ordering on time. Empty means nobody.',
          },
          contractAlertRoles: {
            label: 'Who hears that a supplier contract is about to end',
            help: 'One warning when the contract enters its notice window —to renegotiate in time— and another when it ends. Checked every morning at 7:35. Changing the end date, when renewing it, re-arms both. Empty means nobody.',
          },
          requisitionApproverRoles: {
            label: 'Who hears that a purchase requisition is waiting to be approved',
            help: 'Approving takes the «Approve purchase requisitions» permission; this only decides who is told. Empty means nobody.',
          },
          requisitionThreshold: {
            label: 'Estimate above which a requisition also needs management',
            help: 'Before VAT. At 0, purchasing approves them all.',
          },
          requisitionDirectorRoles: {
            label: 'Who hears that a requisition above the ceiling needs management',
            help: 'The second approval takes the «Approve requisitions above the ceiling» permission.',
          },
          lateAlertRoles: {
            label: 'Who hears that an order has not arrived',
            help: 'The orders whose expected date has passed and that have not fully arrived. Checked every morning at 07:30; one notice says how many there are and which is the oldest. It repeats while they are still late and stops when they are received or cancelled. Empty is nobody.',
          },
        },
      },
    },
    errors: {
      fields: {
        CONTRACT_OTHER_SUPPLIER: 'That contract is with another supplier',
        CONTRACT_NOT_IN_FORCE: 'That contract is not in force on the date of the order',
        ENDS_BEFORE_START: 'It cannot end before it starts',
        INVALID_DATE: 'That is not a date',
      },
    },
    audit: {
      actions: {
        purchasing: {
          supplier_created: 'Supplier filed',
          supplier_updated: 'Supplier edited',
          supplier_archived: 'Supplier archived',
          supplier_restored: 'Supplier brought back on file',
          contact_added: 'Contact added',
          contact_updated: 'Contact edited',
          contact_removed: 'Contact removed',
          contact_primary_set: 'Primary contact changed',
          purchase_created: 'Purchase created',
          purchase_updated: 'Purchase edited',
          purchase_draft: 'Purchase reopened',
          purchase_sent: 'Purchase sent to the supplier',
          purchase_answered: 'Request answered by the supplier',
          purchase_confirmed: 'Order confirmed by the supplier',
          purchase_partial: 'Partial delivery',
          purchase_received: 'Order received',
          purchase_closed: 'Request closed',
          purchase_cancelled: 'Purchase cancelled',
          purchase_archived: 'Purchase archived',
          purchase_restored: 'Purchase brought back on the lists',
          goods_received: 'Goods received',
          request_converted: 'Request turned into an order',
          purchase_repeated: 'Order repeated',
          requisition_created: 'Purchase requisition sent',
          requisition_updated: 'Purchase requisition changed',
          requisition_approved: 'Purchase requisition approved',
          requisition_rejected: 'Purchase requisition rejected',
          requisition_cancelled: 'Purchase requisition withdrawn',
          requisition_converted: 'Purchase requisition turned into a document',
          contract_created: 'Supplier contract created',
          contract_updated: 'Supplier contract updated',
          contract_archived: 'Supplier contract archived',
          contract_restored: 'Supplier contract restored',
        },
      },
    },
  },

  fr: {
    purchasing: {
      title: 'Achats',
      tabs: { overview: 'Fiche' },
      // Las tarjetas del asistente (backend/assistant.js): el borrador, la
      // solicitud de compra y la decisión sobre ella.
      assistant: {
        draft: {
          summary: {
            order: 'Ouvrir un brouillon de commande de {count} lignes pour {supplier}',
            price_request: 'Ouvrir un brouillon de demande de prix de {count} lignes pour {supplier}',
          },
          action: {
            order: 'Ouvrir un brouillon de commande',
            price_request: 'Ouvrir un brouillon de demande de prix',
          },
          where: 'Fournisseur',
          after: 'Il reste en brouillon : rien ne part tant que personne ne l’envoie',
          moreLines: 'Et {count} lignes de plus',
          outcome: 'Brouillon {code} ouvert',
        },
        request: {
          summary: 'Demander aux achats : {title} ({count} lignes)',
          action: 'Demander aux achats',
          noLines: 'Rien à acheter pour l’instant : dites quoi et combien',
          noQuantity: 'Chaque ligne a besoin d’une quantité',
          supplierUnlinked: '« {supplier} » n’est pas dans le fichier fournisseurs : il part en note',
          after: 'Les achats l’approuveront ou diront pourquoi non',
          afterHigh: 'Au-dessus du plafond : après les achats, la direction l’approuve aussi',
          outcome: 'Demande {code} envoyée aux achats',
        },
        approveRequest: {
          summary: 'Approuver la demande {code} · {title}',
          action: 'Approuver la demande d’achat',
          outcome: 'Demande {code} approuvée',
          outcomeHigh: '{code} approuvée par les achats : la direction décide maintenant',
        },
        rejectRequest: {
          summary: 'Refuser la demande {code} · {title}',
          action: 'Refuser la demande d’achat',
          outcome: 'Demande {code} refusée',
        },
        decide: {
          where: 'Demande d’achat',
          notFound: 'La demande n’est pas claire : choisissez-la dans Demandes d’achat',
        },
        field: {
          supplier: 'Fournisseur',
          title: 'Pour quoi',
          neededBy: 'Pour le',
          total: 'Hors taxes',
          destination: 'Où',
          reason: 'À quoi ça sert',
          estimate: 'Estimation',
          request: 'Demande',
          requestedBy: 'Demandée par',
          note: 'Note',
        },
      },

      dashboard: {
        open: 'Commandes en cours',
        openHelp: 'Combien de commandes fournisseur sont engagées et pas entièrement arrivées.',
        committed: 'Engagé',
        committedHelp: 'Ce que totalisent les commandes en cours, en euros : dépense engagée pas encore reçue.',
        suppliers: 'Fournisseurs actifs',
        suppliersHelp: 'Combien de fournisseurs sont actifs ou marqués comme préférés.',
        pendingAmount: 'Dû aux fournisseurs',
        pendingAmountHelp: 'Ce qui a été reçu de fournisseurs qui ne vous ont pas encore facturé, en euros.',
        contractsEnding: 'Contrats qui expirent',
        contractsEndingHelp: 'Les contrats fournisseurs dans leur délai de préavis ou tout juste expirés : ceux à renégocier.',
        requisitions: 'Demandes à approuver',
        requisitionsHelp: 'Les demandes d’achat qui attendent votre approbation.',
        awaiting: 'En attente de marchandise',
        awaitingHelp: 'Les commandes en cours les plus récentes, avec leur fournisseur. Étirez la carte et il en tient plus.',
      },
      actions: {
        newOrder: 'Nouvelle commande', newRequest: 'Demander un prix', newSupplier: 'Nouveau fournisseur',
        newDeliveryNote: 'Bon de livraison sans commande',
        edit: 'Modifier', save: 'Enregistrer', cancel: 'Annuler', close: 'Fermer', remove: 'Supprimer',
        discard: 'Abandonner',
        archive: 'Archiver', restore: 'Remettre au fichier', addLine: 'Ajouter une ligne', pdf: 'Télécharger le PDF',
        send: 'Marquer comme envoyé', confirm: 'Confirmer', receive: 'Réceptionner',
        convert: 'Transformer en commande', cancelDoc: 'Annuler le document', reopen: 'Rouvrir',
        markAnswered: 'Marquer comme répondue', markClosed: 'Clore la demande',
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      purchases: {
        title: 'Achats',
        subtitle: 'Ce que vous avez commandé et ce que vous attendez',
        newTitle: 'Nouvel achat', detailTitle: 'Achat',
        deliveryNoteTitle: 'Bon de livraison sans commande',
        kind: { order: 'Commande', price_request: 'Demande de prix' },
        status: {
          draft: 'Brouillon', sent: 'Envoyé', answered: 'Répondue', confirmed: 'Confirmé',
          partial: 'Partiel', received: 'Réceptionné', closed: 'Close', cancelled: 'Annulé',
        },
        filters: {
          search: 'Rechercher', searchPlaceholder: 'Numéro, fournisseur, référence…',
          kind: 'Type', allKinds: 'Tous', status: 'Statut', allStatuses: 'Tous',
          onlyOpen: 'Ouverts seulement', onlyOverdue: 'En retard seulement',
          only: 'Uniquement les achats de {name}', showAll: 'Tout afficher',
          oneCustomer: 'ce client', oneWork: 'ce chantier',
        },
        table: {
          code: 'Numéro', supplier: 'Fournisseur', title: 'Objet', expected: 'Prévue',
          lines: 'Lignes', total: 'Total', received: 'Reçu', status: 'Statut',
        },
        kpi: {
          open: 'Commandes ouvertes', committed: 'Engagé',
          overdue: 'En retard', received: 'Réceptionnées',
        },
        empty: {
          none: 'Aucun achat pour le moment',
          noneHint: 'Commencez par demander un prix à un fournisseur, ou ouvrez une commande directement.',
          filtered: 'Aucun résultat',
          filteredHint: 'Essayez de retirer un filtre ou de chercher autre chose.',
          noLines: 'Ce document n\'a aucune ligne',
          noLinesHint: 'Ajoutez ce qu\'il faut acheter avant de l\'envoyer.',
        },
        overdueBy: '{days} jours de retard',
        lineOf: '{received} sur {ordered}',
        receivedHint: 'Lignes livrées en entier. Le montant reçu est tenu par le chantier.',
        created: 'Achat créé', saved: 'Achat enregistré',
        statusChanged: 'Statut mis à jour', archived: 'Achat archivé',
        restored: 'Achat remis dans les listes',
        converted: 'Commande {code} créée à partir de la demande',
        blockedSupplier: 'Ce fournisseur est bloqué',
        search: { subtitle: 'Ouvrir le document' },
      },
      suppliers: {
        title: 'Fournisseurs',
        subtitle: 'La fiche de chaque entreprise chez qui vous achetez',
        newTitle: 'Nouveau fournisseur', editTitle: 'Modifier le fournisseur', detailTitle: 'Fiche fournisseur',
        loading: 'Chargement…',
        status: {
          active: 'Actif', preferred: 'Privilégié', inactive: 'Inactif', blocked: 'Bloqué',
        },
        filters: {
          search: 'Rechercher', searchPlaceholder: 'Nom, SIRET, ville…',
          status: 'Statut', allStatuses: 'Tous', category: 'Catégorie', allCategories: 'Toutes',
        },
        table: {
          name: 'Entreprise', taxId: 'N° fiscal', category: 'Catégorie', city: 'Ville',
          contact: 'Contact', leadTime: 'Délai', ordered: 'Acheté', status: 'Statut',
        },
        kpi: {
          active: 'Actifs', preferred: 'Privilégiés', total: 'Total',
          ordered: 'Acheté', invoiced: 'Facturé',
        },
        empty: {
          none: 'Aucun fournisseur pour le moment',
          noneHint: 'Créez le premier : sans fournisseur, rien ne peut être commandé.',
          filtered: 'Aucun résultat',
          filteredHint: 'Essayez de retirer un filtre ou de chercher autre chose.',
          noPurchases: 'Vous ne leur avez encore rien acheté',
        },
        details: 'Coordonnées',
        terms: 'Conditions d\'achat',
        address: 'Adresse',
        activity: 'Activité',
        lastPurchases: 'Derniers achats',
        created: 'Fournisseur créé', saved: 'Fournisseur enregistré',
        archive: {
          title: 'Archiver le fournisseur',
          body: 'Vous allez archiver {name}.',
          keeps: 'Rien n\'est supprimé : il disparaît des listes et son historique reste intact.',
          done: 'Fournisseur archivé',
          restored: 'Fournisseur remis au fichier',
          isArchived: 'Ce fournisseur est archivé : il ne figure sur aucune liste et rien ne peut lui être acheté. Le remettre le replace au fichier, mais INACTIF — lui racheter est la seconde decision.',
          blocked: 'Il a des achats ouverts : clôturez-les ou annulez-les avant de l\'archiver.',
        },
        search: { subtitle: 'Ouvrir sa fiche' },
      },
      related: {
        purchases: 'Achats et réceptions',
        subcontracts: 'Sous-traitance',
        forCustomer: 'Achats',
      },
      spendType: {
        material: 'Matériel', plant: 'Location de matériel',
        subcontract: 'Sous-traitance', service: 'Service', other: 'Autres',
      },
      fields: {
        name: 'Raison sociale', namePlaceholder: 'Nom complet de l\'entreprise',
        shortName: 'Nom court', shortNameHint: 'Celui que vous utilisez dans les listes',
        taxId: 'N° fiscal', status: 'Statut', category: 'Catégorie',
        categoryPlaceholder: 'Ex. : quincaillerie, transport, acier',
        tags: 'Étiquettes', rating: 'Évaluation', noRating: 'Non évalué',
        email: 'Courriel', phone: 'Téléphone', website: 'Site web',
        address: 'Rue', postalCode: 'Code postal', city: 'Ville', country: 'Pays',
        paymentTerms: 'Conditions de paiement', paymentMethod: 'Moyen de paiement', currency: 'Devise',
        discount: 'Remise', discountHint: 'Celle qu\'ils vous font sur tarif, en %',
        leadTime: 'Délai de livraison', leadTimeHint: 'Jours à compter de la commande. 0 si inconnu',
        minOrder: 'Commande minimale', iban: 'IBAN',
        supplierRef: 'Notre code chez eux',
        supplierRefHint: 'Le numéro de compte qu\'ils utilisent pour vous ; il figure sur leurs factures',
        notes: 'Notes', createdAt: 'Créé le',
        contactName: 'Nom', contactPosition: 'Fonction',
        contactPositionPlaceholder: 'Ex. : commercial secteur',
        supplier: 'Fournisseur', customer: 'Client',
        customerHint: 'Si cet achat est destiné à un client précis',
        customerPlaceholder: 'Nom du client',
        work: 'Chantier', workPlaceholder: 'Chercher un chantier',
        workHint: 'Pour quel chantier on achète. Avec ça, le bon de livraison arrive seul dans le coût du chantier au lieu d’être saisi.',
        package: 'Lot', packagePlaceholder: 'Chercher un lot',
        packageHint: 'Contre quel lot de passation. C’est ce qui fait que la commande décompte du bon disponible.',
        spendType: 'Type de dépense',
        spendTypeHint: 'Sépare le matériel de la location et de la sous-traitance, ce qu’on regarde quand un poste dérape.',
        kind: 'Type de document', concept: 'Objet',
        conceptPlaceholder: 'À quoi sert cet achat, en quelques mots',
        reference: 'Référence du fournisseur',
        referenceHint: 'Son devis, sa proforma : ce qu\'il vous a transmis',
        issueDate: 'Date', expectedDate: 'Date prévue',
        receivedAt: 'Date de livraison',
        receivedAtHint: 'Le jour où la marchandise est arrivée. Le coût est de ce jour-là, pas d’aujourd’hui.',
        deliveryAddress: 'Adresse de livraison',
        deliveryHint: 'Laissez vide si la livraison va à votre adresse habituelle',
        invoiceRef: 'Facture du fournisseur',
        notesForSupplier: 'Notes pour le fournisseur',
        notesForSupplierHint: 'Ceci est imprimé sur le document qu\'il reçoit',
        internalNotes: 'Notes internes', internalNotesHint: 'Ceci n\'apparaît pas sur le document',
      },
      lines: {
        title: 'Lignes', ref: 'Réf.', description: 'Désignation',
        quantity: 'Qté', unit: 'Unité', unitPrice: 'Prix', discount: 'Rem.',
        tax: 'TVA', total: 'Total', received: 'Reçu',
        descriptionPlaceholder: 'Ce qui est commandé',
        subtotal: 'Total HT', discountTotal: 'Remises', taxTotal: 'TVA', grandTotal: 'Total',
      },
      contacts: {
        title: 'Personnes à contacter',
        add: 'Ajouter un contact', addTitle: 'Ajouter un contact', editTitle: 'Modifier le contact',
        none: 'Aucun contact pour le moment',
        noneHint: 'La personne que vous appelez quand il se passe quelque chose sur une commande.',
        primary: 'Principal', makePrimary: 'Définir comme principal', markPrimary: 'Marquer comme contact principal',
        primaryHint: 'C\'est le nom qui figure sur la commande. Il ne peut y en avoir qu\'un.',
        removeTitle: 'Supprimer le contact', removeBody: 'Supprimer {name} de ce fournisseur ?',
        removed: 'Contact supprimé', saved: 'Contact enregistré',
      },
      files: {
        title: 'Pièces de la commande',
        add: 'Téléverser un document', uploading: 'Envoi…', uploaded: 'Document envoyé',
        whatIsIt: 'Qu’est-ce que c’est ?',
        kind: { delivery_note: 'Bon de livraison', invoice: 'Facture', other: 'Autre' },
        dropTitle: 'Retirer le document',
        dropConfirm: '« {name} » quitte cette commande.',
        none: {
          title: 'Aucune pièce',
          subtitle: 'Déposez ici le bon de chaque livraison, et la facture à son arrivée.',
        },
      },
      receive: {
        title: 'Réceptionner la marchandise',
        hint: 'Notez ce qui est arrivé sur CE bon de livraison, pas le cumul.',
        pending: 'Restant', arrived: 'Arrive maintenant', all: 'Tout ce qui reste',
        deliveryNote: 'Le bon de livraison',
        deliveryNoteHint: 'Déposez-le maintenant, tant que vous l’avez en main.',
        pickFile: 'Choisir le fichier…',
        date: 'Date du bon de livraison', note: 'Remarque du bon',
        notePlaceholder: 'Ex. : deux cartons abîmés, acceptés',
        done: 'Réception enregistrée',
        complete: 'Commande complète', partial: 'Réception partielle',
        nothing: 'Vous n\'avez rien marqué comme reçu',
      },
      requisitions: {
        title: 'Demandes d’achat',
        subtitle: 'Ce que quelqu’un a besoin qu’on achète, jusqu’à ce que les achats l’approuvent et le commandent',
        new: 'Nouvelle demande',
        newTitle: 'Nouvelle demande d’achat',
        detailTitle: 'Demande d’achat',
        newHint: 'Dites ce dont vous avez besoin : les achats l’examinent et vous préviennent.',
        search: 'Rechercher par numéro, objet ou demandeur…',
        send: 'Envoyer la demande',
        sent: 'Demande {code} envoyée aux achats',
        saved: 'Demande enregistrée',
        approved: 'Demande approuvée',
        rejected: 'Demande refusée',
        cancelled: 'Demande retirée',
        converted: '{code} créé : vérifiez prix et fournisseur avant l’envoi',
        cancelTitle: 'Retirer la demande ?',
        cancelBody: 'La demande {code} n’est plus en attente. Elle reste dans l’historique.',
        scope: {
          toApprove: 'À approuver',
          mine: 'Mes demandes',
          all: 'Toutes',
        },
        empty: {
          toApprove: 'Rien n’attend votre approbation.',
          mine: 'Vous n’avez encore rien demandé.',
          mineHint: 'Quand vous avez besoin qu’on achète quelque chose, demandez-le ici : les achats le voient tout de suite.',
          all: 'Aucune demande.',
        },
        table: {
          what: 'Objet',
          neededBy: 'Pour quand',
          estimate: 'Estimation',
          status: 'Statut',
        },
        status: {
          pending: 'En attente',
          awaiting_direction: 'Attend la direction',
          approved: 'Approuvée',
          rejected: 'Refusée',
          converted: 'Commandée',
          cancelled: 'Retirée',
        },
        statusHint: {
          pending: 'En attente de l’examen des achats.',
          awaiting_direction: 'Les achats l’ont approuvée ; vu son montant, il manque l’accord de la direction.',
          approved: 'Approuvée : les achats la transformeront en demande de prix ou en commande.',
          rejected: 'Elle ne sera pas achetée. Le motif est dans l’historique.',
          converted: 'Déjà en cours dans le document',
          cancelled: 'Retirée avant d’être achetée.',
        },
        fields: {
          title: 'De quoi avez-vous besoin ?',
          titlePlaceholder: 'Meuleuse 230 mm pour l’atelier',
          reason: 'Pour quoi faire',
          reasonHint: 'La première question des achats : répondez ici et évitez l’appel.',
          neededBy: 'Pour quand',
          destination: 'Où ça va',
          destinationHint: 'Une machine, une camionnette, un atelier…',
          destinationPlaceholder: 'Atelier 2',
          supplier: 'Fournisseur suggéré',
          supplierHint: 'Si vous savez qui l’a. Les achats décident.',
        },
        lines: {
          title: 'Ce qui est demandé',
          description: 'Description',
          descriptionPlaceholder: 'Meuleuse Makita 230 mm',
          materialPlaceholder: 'Cherchez dans l’entrepôt ou écrivez le besoin',
          locationPlaceholder: 'Emplacement de destination (facultatif)',
          inStock: 'En stock : {n} {unit}',
          quantity: 'Quantité',
          unit: 'Unité',
          estimate: 'Prix approx.',
          total: 'Montant estimé',
          totalHint: 'Indicatif : le prix réel est fixé par les achats.',
        },
        actions: {
          approve: 'Approuver',
          approveHigh: 'Approuver (direction)',
          reject: 'Refuser',
          convert: 'Transformer en commande…',
          cancel: 'Retirer',
        },
        reject: {
          title: 'Refuser la demande',
          reason: 'Motif',
          placeholder: 'Il y en a déjà une au magasin du bâtiment 1…',
        },
        convert: {
          title: 'Acheter ce qui a été demandé',
          kind: 'Ce qui est créé',
          order: 'Commande',
          priceRequest: 'Demande de prix',
          hint: 'Créé en brouillon avec ces lignes et les prix estimés : vous les vérifiez avant l’envoi.',
          go: 'Créer',
        },
        history: {
          title: 'Historique',
          requested: 'l’a demandée',
          edited: 'l’a modifiée',
          approved_purchasing: 'l’a approuvée (achats)',
          approved: 'l’a approuvée',
          rejected: 'l’a refusée',
          cancelled: 'l’a retirée',
          converted: 'l’a transformée en',
        },
      },
      contracts: {
        title: 'Contrats',
        subtitle: 'Ce qui a été convenu avec chaque fournisseur : prix, montant engagé et validité',
        new: 'Nouveau contrat',
        newTitle: 'Nouveau contrat',
        detailTitle: 'Contrat',
        loading: 'Chargement…',
        created: 'Contrat créé',
        saved: 'Contrat enregistré',
        fields: {
          supplier: 'Fournisseur',
          title: 'Nom du contrat',
          titlePlaceholder: 'Accord-cadre 2026, tarif tuyauterie…',
          reference: 'Leur numéro de contrat',
          referenceHint: 'Celui qui figure sur leur document. Il est imprimé sur chaque commande passée sous ce contrat.',
          startsOn: 'Valable du',
          endsOn: 'Valable jusqu’au',
          noticeDays: 'Prévenir (jours avant)',
          noticeHint: 'Combien de jours avant la fin l’ERP vous prévient, pour renégocier à temps.',
          committedAmount: 'Montant engagé',
          committedHint: 'Ce que vous vous êtes engagés à acheter, hors TVA. Laissez 0 si seuls des prix ont été convenus.',
          notes: 'Notes internes',
        },
        state: {
          upcoming: 'Pas encore en vigueur',
          active: 'En vigueur',
          expiring: 'Expire bientôt',
          expired: 'Expiré',
        },
        table: {
          contract: 'Contrat',
          supplier: 'Fournisseur',
          validity: 'Validité',
          consumed: 'Consommé',
          state: 'Statut',
        },
        kpi: {
          inForce: 'En vigueur',
          expiring: 'Expirent bientôt',
          expired: 'Expirés',
          committed: 'Engagé en vigueur',
        },
        filters: {
          search: 'Rechercher par nom, numéro ou fournisseur…',
          show: 'Afficher',
          all: 'Tous',
          inForce: 'En vigueur seulement',
          archived: 'Archivés',
        },
        empty: {
          none: 'Pas encore de contrats',
          noneHint: 'Enregistrez ce qui a été convenu avec un fournisseur : ses prix s’appliqueront seuls lors de vos commandes.',
          filtered: 'Aucun contrat ne correspond',
          filteredHint: 'Essayez de retirer le filtre ou de chercher autre chose.',
        },
        usage: {
          title: 'Consommation',
          of: '{consumed} sur {committed}',
          remaining: 'Reste {amount}',
          over: 'Dépassé de {amount}',
          noCommitment: 'Sans montant engagé : le contrat fixe seulement des prix.',
          orders: '{n} commande(s) sous ce contrat',
          consumedHint: 'Commandes envoyées ou au-delà, hors TVA. Les brouillons et les annulées ne comptent pas.',
        },
        days: {
          left: 'Reste {n} jours',
          today: 'Se termine aujourd’hui',
          ago: 'Terminé il y a {n} jours',
          startsIn: 'Commence dans {n} jours',
          until: 'Jusqu’au {date}',
          from: 'Depuis le {date}',
        },
        prices: {
          title: 'Prix convenus',
          hint: 'Une ligne de commande avec la même référence —ou, sans référence, la même description— porte ce prix tant que le contrat est en vigueur.',
          add: 'Ajouter un prix',
          fromHistory: 'Depuis les achats précédents',
          none: 'Aucun prix convenu',
          noneHint: 'Ajoutez les références avec leur prix. Un contrat sans prix ne suit que le montant.',
          ref: 'Réf.',
          description: 'Description',
          unit: 'Unité',
          unitPrice: 'Prix',
          discount: 'Rem. %',
          count: '{n} prix convenu(s)',
        },
        files: {
          title: 'Contrat signé',
          add: 'Téléverser le document',
          uploading: 'Téléversement…',
          none: 'Le contrat signé n’a pas encore été téléversé',
          noneHint: 'Téléversez-le en PDF : c’est ce qu’on cherche quand un prix ne correspond pas à la facture.',
          kind: {
            signed: 'Signé',
            other: 'Autre',
          },
          whatIsIt: 'Ce que c’est',
          uploaded: 'Document téléversé',
          dropTitle: 'Retirer le document',
          dropConfirm: 'Retirer « {name} » du contrat ?',
          saveFirst: 'Enregistrez le contrat pour pouvoir téléverser le document signé.',
        },
        purchases: {
          title: 'Commandes passées sous ce contrat',
          none: 'Rien n’a encore été commandé sous ce contrat.',
        },
        archive: {
          title: 'Archiver le contrat',
          body: 'Le contrat {code} n’est plus proposé lors des commandes. Les commandes passées sous ce contrat continuent de l’afficher.',
          done: 'Contrat archivé',
          restored: 'Contrat restauré',
          isArchived: 'Ce contrat est archivé : il n’est pas proposé lors des commandes.',
        },
        inPurchase: {
          field: 'Contrat',
          none: 'Sans contrat',
          hint: 'Ses prix convenus s’appliquent seuls aux lignes qui correspondent.',
          agreed: 'Convenu',
          agreedTitle: 'Prix du contrat {code}',
          remaining: 'Reste {amount} sur {committed} engagés.',
          exceeds: 'Cette commande dépasse de {amount} ce qui reste du contrat.',
          endsOn: 'En vigueur jusqu’au {date}.',
          pickerHeading: 'Prix convenus · {code}',
          open: 'Voir le contrat',
        },
        supplier: {
          title: 'Contrats',
          none: 'Aucun contrat avec ce fournisseur.',
          add: 'Nouveau contrat',
        },
      },
      history: {
        open: 'Depuis les achats précédents',
        title: 'Ce que nous avons déjà acheté',
        search: 'Rechercher par description ou référence…',
        loading: 'Recherche…',
        fromSupplier: 'Acheté chez {name}',
        fromOthers: 'Acheté chez d’autres fournisseurs',
        all: 'Acheté auparavant',
        noneFromSupplier: 'Nous n’avons encore rien acheté de semblable à ce fournisseur.',
        none: 'Aucun résultat.',
        times: '{n} fois',
        last: 'dernière {code}, {date}',
        added: '{n} ajoutée(s) · dernière : {name}',
        done: 'Terminé',
        repeat: 'Répéter la commande',
        repeated: 'Brouillon {code} créé avec les mêmes lignes',
      },
      send: {
        action: 'Envoyer',
        toSupplier: 'Envoyer au fournisseur',
        again: 'Renvoyer',
      },
      // Lo que «Enviar por email» pone de asunto en el redactor de la plataforma
      // (use-cases/PurchaseMail.js → subjectKey).
      mail: {
        subject: {
          order: 'Bon de commande {code}',
          request: 'Demande de prix {code}',
        },
      },
      cancel: {
        title: 'Annuler le document',
        body: 'Vous allez annuler {code}.',
        reason: 'Motif', reasonHint: 'Il reste écrit : c\'est ce qui évite qu\'on le recommande dans un mois.',
        reasonPlaceholder: 'Ex. : le fournisseur ne peut pas livrer dans les délais',
        done: 'Document annulé',
      },
      document: {
        order: 'Bon de commande', priceRequest: 'Demande de prix',
        date: 'Date', expected: 'Nécessaire pour le', supplier: 'Fournisseur',
        ourRef: 'Notre référence', yourRef: 'Votre référence', deliverTo: 'Livrer à',
        contract: 'Contrat',
        taxId: 'N° fiscal', ref: 'Réf.', description: 'Désignation', quantity: 'Qté',
        unitPrice: 'Prix', discount: 'Rem.', total: 'Total', subtotal: 'Total HT',
        tax: 'TVA', grandTotal: 'Total', paymentTerms: 'Conditions de paiement', notes: 'Notes',
        priceRequestNote: 'Ceci est une demande de prix. Ce n\'est pas une commande.',
      },
      notifications: {
        stockAlert: 'Article sous son minimum',
        lateOrder: 'Une commande qui n\'est pas arrivée',
        contractExpiry: 'Un contrat fournisseur qui arrive à son terme',
        contract: {
          notice: {
            title: 'Le contrat {code} expire bientôt',
            body: '« {title} » avec {supplier} se termine le {endsOn} : il reste {days} jours pour le renégocier.',
          },
          expired: {
            title: 'Le contrat {code} a expiré',
            body: '« {title} » avec {supplier} s’est terminé le {endsOn}. Les nouvelles commandes ne portent plus ses prix.',
          },
        },
        requisitionCategory: 'Demandes d’achat',
        requisition: {
          toApprove: {
            title: 'Demande {code} à approuver',
            body: '{who} demande : {title}.',
          },
          toDirection: {
            title: 'Demande {code} : attend la direction',
            body: '{title} ({who}) dépasse le plafond et attend votre approbation.',
          },
          approved: {
            title: 'Votre demande {code} est approuvée',
            body: '{title} : les achats vont s’en occuper.',
          },
          rejected: {
            title: 'Votre demande {code} ne sera pas achetée',
            body: '{title}. Motif : {extra}',
          },
          converted: {
            title: 'Votre demande {code} est en cours',
            body: '{title} : elle est dans le document {extra}.',
          },
        },
        lateOrders: {
          title: 'Des commandes ne sont pas arrivées',
          body: '{count} commande(s) ont dépassé leur date. La plus ancienne, {code} de {supplier}, a {days} jour(s) de retard.',
        },
        lowStock: {
          title: 'Article sous son minimum',
          body: '{material} : il en reste {stock}, le minimum est {minimum}.',
        },
        planAlert: 'Le plan annonce qu\'il manquera quelque chose',
        planRisk: {
          material: 'Il manquera de la matière',
          lead_time: 'Le délai du fournisseur ne suffit plus',
          supplier: 'Risque fournisseur',
          body: '{subject} — {period}, selon le plan {plan}.',
        },
      },
    },

    modules: { purchasing: 'Achats' },
    assistant: {
      openers: {
        purchasing: {
          'whats-late': 'Qu\'attendons-nous qui n\'est pas arrivé ?',
          'spend-this-quarter': 'Combien avons-nous acheté ce trimestre, et à qui ?',
          'draft-an-order': 'Prépare-moi un brouillon de commande',
          'ask-to-buy': 'Demande aux achats quelque chose dont j’ai besoin',
          'requests-waiting': 'Quelles demandes d’achat m’attendent ?',
        },
      },
    },
    permissions: {
      purchasing: {
        read: 'Voir les fournisseurs et les achats',
        write: 'Créer et modifier des achats',
        send: 'Envoyer une commande à un fournisseur',
        receive: 'Réceptionner la marchandise',
        suppliers: 'Tenir la fiche des fournisseurs',
        request: 'Demander un achat (sans pouvoir acheter)',
        approve: 'Approuver les demandes d’achat',
        approve_high: 'Approuver les demandes au-dessus du plafond',
        award: 'Attribuer les comparatifs',
      },
    },
    settings: {
      mail: {
      },
      ns: {
        purchasing: {
          title: 'Achats',
          subtitle: 'Avec quelles données naissent un fournisseur et un achat',
          defaultCountry: { label: 'Pays d\'un nouveau fournisseur' },
          defaultCurrency: {
            label: 'Devise dans laquelle un nouveau fournisseur vous facture',
            help: 'ISO 4217, trois lettres. Modifiable ensuite fournisseur par fournisseur.',
          },
          defaultPaymentTerms: { label: 'Conditions de paiement d\'un nouveau fournisseur' },
          defaultTaxRate: {
            label: 'TVA d\'un nouvel achat',
            help: 'Le taux hérité par chaque ligne sauf mention contraire. 0, 4, 10 ou 21.',
          },
          defaultLeadTimeDays: {
            label: 'Délai supposé quand le fournisseur n\'en a aucun',
            help: 'Sert à proposer la date prévue d\'une commande. Laissez 0 pour ne rien proposer.',
          },
          requireSupplierTaxId: {
            label: 'Exiger un numéro fiscal pour tout fournisseur',
            help: 'Laissez désactivé si la quincaillerie du coin est enregistrée avec un téléphone.',
          },
          alertRoles: {
            label: 'Qui est averti qu\'un article passe sous son minimum',
            help: 'N\'a d\'effet qu\'avec le module de stock installé.',
          },
          planAlertRoles: {
            label: 'Qui est averti qu\'il manquera quelque chose au plan',
            help: 'N\'a d\'effet qu\'avec le module de planification S&OP installé. Une liste distincte de celle du dessus : celle-ci avertit de ce qui est déjà épuisé, celle-là de ce qui le sera — la seule que l\'on puisse encore résoudre en commandant à temps. Vide signifie personne.',
          },
          contractAlertRoles: {
            label: 'Qui est prévenu qu’un contrat fournisseur arrive à son terme',
            help: 'Un avis quand le contrat entre dans son délai de préavis —pour renégocier à temps— et un autre quand il se termine. Vérifié chaque matin à 7h35. Changer la date de fin, au renouvellement, réarme les deux. Vide signifie personne.',
          },
          requisitionApproverRoles: {
            label: 'Qui est prévenu qu’une demande d’achat attend une approbation',
            help: 'Approuver demande la permission « Approuver les demandes d’achat » ; ceci décide seulement qui est prévenu. Vide signifie personne.',
          },
          requisitionThreshold: {
            label: 'Montant estimé au-delà duquel une demande nécessite aussi la direction',
            help: 'Hors TVA. À 0, les achats les approuvent toutes.',
          },
          requisitionDirectorRoles: {
            label: 'Qui est prévenu qu’une demande au-dessus du plafond attend la direction',
            help: 'La seconde approbation demande la permission « Approuver les demandes au-dessus du plafond ».',
          },
          lateAlertRoles: {
            label: 'Qui est informé qu\'une commande n\'est pas arrivée',
            help: 'Les commandes dont la date prévue est passée et qui ne sont pas entièrement arrivées. Vérifié chaque matin à 07h30 ; un avis indique combien il y en a et laquelle est la plus ancienne. Il se répète tant qu\'elles sont en retard et cesse quand elles sont reçues ou annulées. Vide, personne.',
          },
        },
      },
    },
    errors: {
      fields: {
        CONTRACT_OTHER_SUPPLIER: 'Ce contrat est avec un autre fournisseur',
        CONTRACT_NOT_IN_FORCE: 'Ce contrat n’est pas en vigueur à la date de la commande',
        ENDS_BEFORE_START: 'Il ne peut pas se terminer avant de commencer',
        INVALID_DATE: 'Ce n’est pas une date',
      },
    },
    audit: {
      actions: {
        purchasing: {
          supplier_created: 'Fournisseur créé',
          supplier_updated: 'Fournisseur modifié',
          supplier_archived: 'Fournisseur archivé',
          supplier_restored: 'Fournisseur remis au fichier',
          contact_added: 'Contact ajouté',
          contact_updated: 'Contact modifié',
          contact_removed: 'Contact supprimé',
          contact_primary_set: 'Contact principal modifié',
          purchase_created: 'Achat créé',
          purchase_updated: 'Achat modifié',
          purchase_draft: 'Achat rouvert',
          purchase_sent: 'Achat envoyé au fournisseur',
          purchase_answered: 'Demande répondue par le fournisseur',
          purchase_confirmed: 'Commande confirmée par le fournisseur',
          purchase_partial: 'Réception partielle',
          purchase_received: 'Commande réceptionnée',
          purchase_closed: 'Demande close',
          purchase_cancelled: 'Achat annulé',
          purchase_archived: 'Achat archivé',
          purchase_restored: 'Achat remis dans les listes',
          goods_received: 'Marchandise réceptionnée',
          request_converted: 'Demande transformée en commande',
          purchase_repeated: 'Commande répétée',
          requisition_created: 'Demande d’achat envoyée',
          requisition_updated: 'Demande d’achat modifiée',
          requisition_approved: 'Demande d’achat approuvée',
          requisition_rejected: 'Demande d’achat refusée',
          requisition_cancelled: 'Demande d’achat retirée',
          requisition_converted: 'Demande d’achat transformée en document',
          contract_created: 'Contrat fournisseur créé',
          contract_updated: 'Contrat fournisseur modifié',
          contract_archived: 'Contrat fournisseur archivé',
          contract_restored: 'Contrat fournisseur restauré',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'
// Y los del comparativo de ofertas, que vinieron de Obras (0.12.0). Ver ese fichero.
import { withTenders } from './i18n/tenders.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(withTenders(baseMessages), 'purchasing')))
